import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
  useDeferredValue,
  memo,
} from "react";

import {
  Box,
  Typography,
  Divider,
  TextField,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  Skeleton,
  Stack,
  Chip,
  InputAdornment,
  Tooltip,
  MenuItem,
  Popover,
  Badge,
  Button,
  Select,
  LinearProgress,
} from "@mui/material";
import dayjs from "dayjs";
import NotesIcon from "@mui/icons-material/Notes";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from "../Sidebar/SidebarProvider";

import TimelineIcon from "@mui/icons-material/Timeline";
import CriticalPathDrawer from "./CriticalPathDrawer";

import {
  getEntityInitial,
  getSupplierColor,
  getClientColor,
} from "./criticalPathAvatars";

import {
  getNotasByEntityWithUser,
  getNotasNuevas,
  getNotasResumenWithFallback,
  getNotasResumenClientes,
  marcarNotasLeidasPorEntity,
} from "../../api/CriticalPath/criticalPathNotasApi";

import CpEntityChat, { getCpChatMetaKey } from "./CpEntityChat";
import CpChatDot from "./CpChatDot";
import { getEntityChatSummary } from "../../api/Chat/entityChatApi";
import { setBrowserTabBadge } from "../../utils/browserTabBadge";
import { socket } from "../../socket";

import { useTranslation } from "react-i18next";
import { pygHeroShellSx } from "../PyGAnalitico/PygAnaliticoPageHeader";

import { jwtDecode } from "jwt-decode";
import {
  getUsersWithAddNotesPermission,
} from "../../api/User/userApi";

import {
  getCriticalPathHorizontal,
  getCriticalPathHorizontal1,
  getCriticalPathMuestras,
  getTimelineByProductId,
  getPresupuestoInfoByProductId,
  getTimelinesBulkForHub,
  setCriticalPathEntityClosed,
} from "../../api/Estimates/estimatesApi";

import ProductPhotosCell from "./ProductPhotosCell";
import {
  findMuestraTask,
  groupMuestras,
} from "./criticalPathMuestraUtils";
import {
  findCpRowForHubBlock,
  getLegacyPoDateFromRow,
  extractLegacyLogistics,
  prodRowsExcludingMuestras,
  resolveCpIdPresupuestoFromRow,
  withLegacyLogisticsFlag,
} from "./criticalPathEntityUtils";
import PresupuestoSemaforo from "./PresupuestoSemaforo";
import {
  enrichFechaWithSemaforo,
  muiColorFromSemaforo,
  scoreBlockSemaforo,
  semaforoLevelWeight,
  SEMAFORO_LEVELS,
} from "./computePhaseSemaforo";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

/** Concurrencia HTTP para adjuntos al abrir el diálogo. */
const NOTAS_RESUMEN_ADJUNTOS_CONCURRENCY = 8;
/** Fechas: tamaño de lote al endpoint bulk-for-hub. */
const NOTAS_RESUMEN_FECHAS_BULK_SIZE = 25;
/** Cache key for the full hub (cliente is a local filter). */
const HUB_CACHE_ALL_KEY = "__ALL__";
/** Columnas de fases siempre visibles (aunque aún no hayan cargado). */
const FECHA_PHASE_KEYS = [
  "PO",
  "PP_SEND",
  "OK_PRODUCT",
  "TOP_APPROVED",
  "FABRIC",
  "PROD_START",
  "INSP_DATE",
  "ExFD",
  "ETD",
  "DD",
  "ETA",
];
const EMPTY_FECHAS_PLACEHOLDER = Object.fromEntries(
  FECHA_PHASE_KEYS.map((key) => [key, { date: null, source: null }]),
);

const isEntityFechasLoaded = (fechasByKey, entityKey) =>
  Boolean(fechasByKey?.[entityKey]?.loaded);

const poDatesMatch = (left, right) => {
  if (left == null || right == null) return false;
  const a = dayjs(left);
  const b = dayjs(right);
  return a.isValid() && b.isValid() && a.isSame(b, "day");
};

const getBlockCliente = (block) =>
  String(block?.productInfo?.Cliente || "").trim() || "—";

const getBlockProveedor = (block) =>
  String(block?.productInfo?.Proveedor || "").trim() || "—";

/** Estado comercial para filtros (Created / Offered / Accepted / MUESTRA). */
const getBlockPresupuestoEstado = (block) => {
  const estado = String(block?.presupuestoEstado || "").trim();
  if (estado) return estado;
  if (block?.idMuestra) return "MUESTRA";
  return null;
};

const PRESUPUESTO_ESTADO_FILTER_VALUES = [
  "ALL",
  "Created",
  "Offered",
  "Accepted",
  "MUESTRA",
];

const countOpenIssuesInNotas = (notas) =>
  (notas || []).filter(
    (n) => n.TipoNota === "Open Issue" && n.Status !== "Closed",
  ).length;

const getBlockNoteCount = (block) => {
  if (!block) return 0;
  if (block.notasLoaded && Array.isArray(block.notas)) {
    return block.notas.length;
  }
  return Number(block.noteCount) || 0;
};

const getBlockOpenIssueCount = (block) => {
  if (!block) return 0;
  if (block.notasLoaded && Array.isArray(block.notas)) {
    return countOpenIssuesInNotas(block.notas);
  }
  return Number(block.openIssueCount) || 0;
};

/** Rojo = nuevas · Naranja = incidencias abiertas · Verde = notas normales */
const getNoteBadgeState = (block, unreadCount) => {
  const noteCount = getBlockNoteCount(block);
  const openIssueCount = getBlockOpenIssueCount(block);

  if (noteCount === 0) {
    return { variant: "empty", noteCount, unreadCount, openIssueCount };
  }
  if (unreadCount > 0) {
    return { variant: "unread", noteCount, unreadCount, openIssueCount };
  }
  if (openIssueCount > 0) {
    return { variant: "issue", noteCount, unreadCount, openIssueCount };
  }
  return { variant: "normal", noteCount, unreadCount, openIssueCount };
};

const compareBlocksByCliente = (blockA, blockB) => {
  const clientCmp = getBlockCliente(blockA).localeCompare(
    getBlockCliente(blockB),
    undefined,
    { sensitivity: "base" },
  );
  if (clientCmp !== 0) return clientCmp;

  const scoreDiff =
    scoreBlockSemaforo(blockB.fechas) - scoreBlockSemaforo(blockA.fechas);
  if (scoreDiff !== 0) return scoreDiff;

  const designA =
    blockA.productInfo?.Design ||
    blockA.productInfo?.NumeroPresupuesto ||
    blockA.entityKey ||
    "";
  const designB =
    blockB.productInfo?.Design ||
    blockB.productInfo?.NumeroPresupuesto ||
    blockB.entityKey ||
    "";
  return designA.localeCompare(designB, undefined, { sensitivity: "base" });
};

const filterBlockNotas = (
  notas,
  { onlyOpenIssues, tipoNota, status, responsible },
) =>
  (notas || []).filter((n) => {
    if (onlyOpenIssues && n.TipoNota !== "Open Issue") return false;
    if (tipoNota !== "ALL" && n.TipoNota !== tipoNota) return false;
    if (status !== "ALL" && n.Status !== status) return false;
    if (responsible !== "ALL" && n.Responsible !== responsible) return false;
    return true;
  });

/** Map async con tope de simultaneidad. */
async function mapPool(items, limit, worker) {
  if (!items?.length) return [];
  const cap = Math.min(Math.max(1, limit), items.length);
  const out = new Array(items.length);
  let next = 0;
  const runWorker = async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) break;
      out[i] = await worker(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: cap }, () => runWorker()));
  return out;
}

/** Dispara carga de fechas cuando la fila entra en viewport (o cerca). */
const VisibleFechasTrigger = memo(function VisibleFechasTrigger({
  entityKey,
  enabled,
  onVisible,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible(entityKey);
          observer.disconnect();
        }
      },
      { rootMargin: "480px 0px", threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [entityKey, enabled, onVisible]);

  return (
    <Box
      ref={ref}
      aria-hidden
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 1,
        height: 1,
        pointerEvents: "none",
      }}
    />
  );
});

const NotasResumenFilterRow = memo(function NotasResumenFilterRow({
  label,
  value,
  onChange,
  active,
  children,
  theme,
  selectMenuProps,
  allLabel,
  formatValue,
}) {
  const display = formatValue
    ? formatValue(value)
    : !value || value === "ALL"
      ? allLabel
      : String(value);

  const handleChange = (event) => {
    onChange(event);
    requestAnimationFrame(() => {
      event.target?.blur?.();
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "96px 1fr", sm: "108px 1fr" },
        alignItems: "center",
        gap: 1.25,
        py: 0.75,
        px: 1,
        borderRadius: 1.5,
        borderLeft: "3px solid",
        borderLeftColor: active
          ? theme.palette.primary.main
          : "transparent",
        transition: "border-color 0.15s ease",
        "&:hover": {
          bgcolor: alpha(theme.palette.action.hover, 0.04),
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: active ? 800 : 700,
          fontSize: "0.72rem",
          lineHeight: 1.25,
          color: active ? theme.palette.primary.dark : "text.secondary",
        }}
      >
        {label}
      </Typography>
      <Select
        value={value}
        onChange={handleChange}
        size="small"
        fullWidth
        displayEmpty
        MenuProps={selectMenuProps}
        renderValue={() => (
          <Typography
            component="span"
            noWrap
            title={display}
            sx={{
              display: "block",
              fontSize: "0.8125rem",
              fontWeight: active ? 700 : 500,
              color: active ? "text.primary" : "text.secondary",
            }}
          >
            {display}
          </Typography>
        )}
        sx={{
          minWidth: 0,
          borderRadius: 1.25,
          fontSize: "0.8125rem",
          bgcolor: theme.palette.background.paper,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.divider, 0.55),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.primary.main, 0.45),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
          },
          "& .MuiSelect-select": {
            py: 0.875,
            pr: "28px !important",
          },
        }}
      >
        {children}
      </Select>
    </Box>
  );
});

/**
 * NotasResumen
 * Vista global de notas agrupadas por producto
 */
const NotasResumen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const { t } = useTranslation();

  const [grouped, setGrouped] = useState({});
  const [fechasByKey, setFechasByKey] = useState({});
  const [hubLoading, setHubLoading] = useState(false);
  const [hubLoadError, setHubLoadError] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [hubClientes, setHubClientes] = useState([]);
  const [hubCliente, setHubCliente] = useState("");
  const userIdRef = useRef(null);
  const hubCacheRef = useRef(new Map());
  const hubClienteRef = useRef("");
  const hubLoadGenRef = useRef(0);
  useEffect(() => {
    hubClienteRef.current = hubCliente;
  }, [hubCliente]);

  const patchHubCache = useCallback((patch) => {
    const cached = hubCacheRef.current.get(HUB_CACHE_ALL_KEY);
    if (!cached) return;
    hubCacheRef.current.set(HUB_CACHE_ALL_KEY, { ...cached, ...patch });
  }, []);
  const groupedRef = useRef({});
  const fechasByKeyRef = useRef({});
  const fechasQueueRef = useRef(new Set());
  const fechasInflightRef = useRef(new Set());
  const fechasFlushTimerRef = useRef(null);
  const fechasFlushRunningRef = useRef(false);
  const flushFechasQueueRef = useRef(async () => {});

  // filtros
  const [tipoNota, setTipoNota] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [responsible, setResponsible] = useState("ALL");
  const [onlyOpenIssues] = useState(false);

  const [showClosedEntities, setShowClosedEntities] = useState(false);
  const [onlyWithNotes, setOnlyWithNotes] = useState(false);
  const [onlyAlert, setOnlyAlert] = useState(false);
  const [presupuestoEstadoFilter, setPresupuestoEstadoFilter] = useState("ALL");

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [proveedorFilter, setProveedorFilter] = useState("ALL");
  const [users, setUsers] = useState([]);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
  const [legendAnchorEl, setLegendAnchorEl] = useState(null);

  // ACORDEON
  const [openCards, setOpenCards] = useState({});

  const markEntityReadInBackground = (entityKey) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const block = groupedRef.current[entityKey];
    if (!block) return;

    const { userId } = jwtDecode(token);
    marcarNotasLeidasPorEntity({
      productId: block.productId,
      idMuestra: block.idMuestra,
      idPresupuestoA3Pedido: block.idPresupuestoA3Pedido,
      userId,
    })
      .then(() => window.dispatchEvent(new Event("notas-leidas")))
      .catch((e) => console.error("❌ Error marcando notas leídas", e));
  };

  const loadNotasForEntity = async (entityKey) => {
    const block = groupedRef.current[entityKey];
    if (!block || block.notasLoading) return;

    const token = localStorage.getItem("token");
    if (!token) return;
    const { userId } = jwtDecode(token);

    setGrouped((prev) => {
      if (!prev[entityKey]) return prev;
      return {
        ...prev,
        [entityKey]: { ...prev[entityKey], notasLoading: true },
      };
    });

    try {
      const notas = await getNotasByEntityWithUser({
        productId: block.productId,
        idMuestra: block.idMuestra,
        idPresupuestoA3Pedido: block.idPresupuestoA3Pedido,
        userId,
      });

      setGrouped((prev) => {
        if (!prev[entityKey]) return prev;
        const next = {
          ...prev,
          [entityKey]: {
            ...prev[entityKey],
            notas: notas || [],
            notasLoading: false,
            notasLoaded: true,
            noteCount: (notas || []).length,
            openIssueCount: countOpenIssuesInNotas(notas),
          },
        };
        groupedRef.current = next;
        return next;
      });
    } catch (err) {
      console.error("❌ Error cargando notas entidad", entityKey, err);
      setGrouped((prev) => {
        if (!prev[entityKey]) return prev;
        return {
          ...prev,
          [entityKey]: { ...prev[entityKey], notasLoading: false },
        };
      });
    }
  };

  const toggleCard = (entityKey) => {
    const willOpen = !(openCards[entityKey] ?? false);

    setOpenCards((prev) => ({
      ...prev,
      [entityKey]: willOpen,
    }));

    if (willOpen) {
      const block = groupedRef.current[entityKey];
      if (block && !isEntityFechasLoaded(fechasByKeyRef.current, entityKey)) {
        loadFechasForEntity(entityKey);
      }
      if (block && !block.notasLoaded) {
        loadNotasForEntity(entityKey);
      }
    }

    setUnreadSnapshot((prev) => {
      const copy = new Set(prev);
      const block = groupedRef.current[entityKey];

      if (block?.notas) {
        block.notas.forEach((n) => copy.delete(n.Id));
      }

      return copy;
    });

    if (willOpen) {
      markEntityReadInBackground(entityKey);
    }
  };

  // ACORDEON

  // FOTOS PRODUCTO

  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // FOTOS PRODUCTO
  const [productsWithAttachments, setProductsWithAttachments] = useState(
    new Set(),
  );

  // Adjuntos (dialog global)
  const [attachmentsDialogOpen, setAttachmentsDialogOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);

  // Chat + Notas (unified drawer)
  const [cpChatState, setCpChatState] = useState({
    open: false,
    productId: null,
    idPresupuestoA3Pedido: null,
    title: "",
    subtitle: "",
    initialTab: "notas",
  });
  const [chatMetaMap, setChatMetaMap] = useState({});

  const [closeConfirmState, setCloseConfirmState] = useState({
    open: false,
    block: null,
  });

  const [unreadSnapshot, setUnreadSnapshot] = useState(new Set());
  const [unreadCountByEntityKey, setUnreadCountByEntityKey] = useState({});

  /** Solo cambia cuando cambian entidades o lista de ids de notas — no en cada parche de fechas (fase 2). */
  const groupedNotasFingerprint = useMemo(() => {
    const keys = Object.keys(grouped).sort();
    if (!keys.length) return "";
    return keys
      .map((k) => {
        const block = grouped[k];
        const ids = (block?.notas || []).map((n) => n.Id).join(",");
        return `${k}:${ids}`;
      })
      .join("|");
  }, [grouped]);

  // cargar usuarios (para filtro responsible)
  useEffect(() => {
    getUsersWithAddNotesPermission().then(setUsers);
  }, []);

  const loadChatSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { userId } = jwtDecode(token);
      const prodSummary = await getEntityChatSummary("Producto", userId);
      const normalized = {};
      Object.entries(prodSummary || {}).forEach(([id, meta]) => {
        normalized[`Producto:${Number(id)}`] = {
          total: Number(meta.total) || 0,
          noLeidos: Number(meta.noLeidos) || 0,
          hasMessages: (Number(meta.total) || 0) > 0,
          hasMention: false,
        };
      });
      setChatMetaMap(normalized);
    } catch (error) {
      console.error("Error loading NotasResumen chat summary:", error);
    }
  }, []);

  const clearChatUnreadForKey = useCallback((metaKey) => {
    if (!metaKey) return;
    setChatMetaMap((prev) => {
      const cur = prev[metaKey];
      if (!cur) return prev;
      const next = {
        ...prev,
        [metaKey]: { ...cur, noLeidos: 0, hasMention: false },
      };
      const anyMention = Object.values(next).some((m) => m?.hasMention);
      if (!anyMention) setBrowserTabBadge(false);
      return next;
    });
  }, []);

  const openCpChatFromBlock = useCallback(
    (block, tab = "mensajes") => {
      if (!block?.productId && !block?.idPresupuestoA3Pedido) return;
      const productId = block.productId ?? null;
      const idPresupuestoA3Pedido = block.idPresupuestoA3Pedido ?? null;
      const pi = block.productInfo || {};
      setCpChatState({
        open: true,
        productId,
        idPresupuestoA3Pedido,
        title:
          pi.Design ||
          pi.NumeroPresupuesto ||
          (idPresupuestoA3Pedido
            ? `Pedido ${idPresupuestoA3Pedido}`
            : `Product ${productId || ""}`),
        subtitle: [pi.Cliente, pi.Proveedor].filter(Boolean).join(" · "),
        initialTab: tab,
      });
      clearChatUnreadForKey(
        getCpChatMetaKey({ productId, idPresupuestoA3Pedido }),
      );
    },
    [clearChatUnreadForKey],
  );

  const getBlockChatMeta = useCallback(
    (block) => {
      const key = getCpChatMetaKey({
        productId: block?.productId,
        idPresupuestoA3Pedido: block?.idPresupuestoA3Pedido,
      });
      const meta = (key && chatMetaMap[key]) || {};
      return {
        total: Number(meta.total) || 0,
        noLeidos: Number(meta.noLeidos) || 0,
        hasMention: Boolean(meta.hasMention),
        hasMessages:
          Boolean(meta.hasMessages) || (Number(meta.total) || 0) > 0,
      };
    },
    [chatMetaMap],
  );

  useEffect(() => {
    loadChatSummary();
  }, [loadChatSummary]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const currentUserId = token ? jwtDecode(token)?.userId : null;
    const openKey = cpChatState.open
      ? getCpChatMetaKey({
          productId: cpChatState.productId,
          idPresupuestoA3Pedido: cpChatState.idPresupuestoA3Pedido,
        })
      : null;

    const handleChatNuevo = ({
      entityType,
      entityId,
      mentionedUserIds,
      mensaje,
    }) => {
      if (entityType !== "Producto" && entityType !== "PedidoA3") return;
      const metaKey = getCpChatMetaKey({ entityType, entityId });
      if (!metaKey) return;
      const openHere = Boolean(openKey && openKey === metaKey);
      const isMentioned =
        Number(mensaje?.AutorId) !== Number(currentUserId) &&
        (mentionedUserIds || []).map(Number).includes(Number(currentUserId));

      setChatMetaMap((prev) => {
        const cur = prev[metaKey] || {
          total: 0,
          noLeidos: 0,
          hasMention: false,
        };
        return {
          ...prev,
          [metaKey]: {
            total: (cur.total || 0) + 1,
            noLeidos: openHere ? 0 : (cur.noLeidos || 0) + 1,
            hasMessages: true,
            hasMention: openHere
              ? false
              : Boolean(cur.hasMention) || isMentioned,
          },
        };
      });

      if (isMentioned && !openHere) {
        setBrowserTabBadge(true);
      }
    };

    const handleChatLeidos = ({ entityType, entityId, userId: readBy }) => {
      if (entityType !== "Producto" && entityType !== "PedidoA3") return;
      if (
        readBy != null &&
        currentUserId != null &&
        Number(readBy) !== Number(currentUserId)
      ) {
        return;
      }
      const metaKey = getCpChatMetaKey({ entityType, entityId });
      if (!metaKey) return;
      setChatMetaMap((prev) => {
        const cur = prev[metaKey];
        if (!cur) return prev;
        return {
          ...prev,
          [metaKey]: { ...cur, noLeidos: 0, hasMention: false },
        };
      });
      setBrowserTabBadge(false);
    };

    socket.on("chat-nuevo-mensaje", handleChatNuevo);
    socket.on("chat-mensajes-leidos", handleChatLeidos);
    return () => {
      socket.off("chat-nuevo-mensaje", handleChatNuevo);
      socket.off("chat-mensajes-leidos", handleChatLeidos);
    };
  }, [
    cpChatState.open,
    cpChatState.productId,
    cpChatState.idPresupuestoA3Pedido,
  ]);

  // getNotasNuevas solo cuando cambian entidades o ids de notas, no en cada parche de fechas (fase 2).
  useEffect(() => {
    const buildUnreadSnapshot = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      const { userId } = jwtDecode(token);

      const data = await getNotasNuevas(userId);
      const unreadCounts = {};

      for (const row of data.porProducto || []) {
        const entityKey =
          row.EntityKey ||
          (row.ProductId
            ? `P_${row.ProductId}`
            : row.ID_MUESTRA
              ? `M_${row.ID_MUESTRA}`
              : null);
        if (!entityKey) continue;
        unreadCounts[entityKey] = Number(row.count) || 0;
      }

      setUnreadCountByEntityKey(unreadCounts);

      const unread = new Set();

      Object.values(grouped).forEach((block) => {
        if (!unreadCounts[block.entityKey]) return;
        (block.notas || []).forEach((n) => unread.add(n.Id));
      });

      setUnreadSnapshot(unread);
    };

    if (groupedNotasFingerprint) {
      buildUnreadSnapshot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- grouped omitido a propósito: ver comentario arriba del efecto
  }, [groupedNotasFingerprint]);

  // DRAWER FECHAS

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProductId, setDrawerProductId] = useState(null);
  const [drawerIdPresupuesto, setDrawerIdPresupuesto] = useState(null);
  const [drawerIdPresupuestoA3Pedido, setDrawerIdPresupuestoA3Pedido] =
    useState(null);
  const [drawerLegacyLogistics, setDrawerLegacyLogistics] = useState([]);
  const [drawerHasLegacyLogistics, setDrawerHasLegacyLogistics] =
    useState(false);
  const [drawerGridRowSnapshot, setDrawerGridRowSnapshot] = useState(null);

  const [drawerIdMuestra, setDrawerIdMuestra] = useState(null);
  const [drawerMode, setDrawerMode] = useState("PRODUCCION");
  const [drawerMuestraHeader, setDrawerMuestraHeader] = useState(null);

  const buildMuestraHeaderFromBlock = (block) => {
    const pi = block?.productInfo || {};
    const labelParts = [pi.Cliente, pi.Temporada].filter(Boolean);
    return {
      DiseñoMuestra: pi.Design || null,
      designCliente: pi.Cliente || null,
      NombrePresupuesto: labelParts.length ? labelParts.join(" - ") : null,
      NumeroPresupuesto: pi.NumeroPresupuesto || null,
      FechaAceptacion: block?.etaFromLogistics || null,
    };
  };

  // DRAWER FECHAS

  // Meto las fechas del timeline en un objeto para usarlas luego
  const getBestDate = (t) => {
    if (!t) return { date: null, source: null };

    if (t.RealDate) {
      return { date: t.RealDate, source: "REAL" };
    }

    if (t.UpdatedDate) {
      return { date: t.UpdatedDate, source: "UPDATED" };
    }

    if (t.ExpectedDate) {
      return { date: t.ExpectedDate, source: "EXPECTED" };
    }

    return { date: null, source: null };
  };

  const phaseFromTimeline = (timelineMap, taskName) => {
    const task = timelineMap?.[taskName];
    return enrichFechaWithSemaforo(getBestDate(task), task);
  };

  const phaseFromMuestraTask = (row, taskName) => {
    const task = findMuestraTask(row, taskName);
    if (!task) {
      return enrichFechaWithSemaforo({ date: null, source: null }, null);
    }
    const taskRow = {
      RealDate: task.real,
      UpdatedDate: task.updated,
      ExpectedDate: task.expected,
    };
    return enrichFechaWithSemaforo(getBestDate(taskRow), taskRow);
  };

  /** Hub usa IdPresupuesto como productId; el API de timeline lo necesita explícito. */
  const getProductionTimelineOpts = (block) => {
    if (!block?.productId || block.idMuestra || block.idPresupuestoA3Pedido) {
      return {};
    }
    return { idPresupuesto: block.productId };
  };

  const getPresupuestoPoDate = (presupuesto) =>
    presupuesto?.FechaAceptacion ||
    presupuesto?.FechaPresentacion ||
    presupuesto?.FechaCrear ||
    null;

  const buildProductionFechas = (
    timelineMap,
    presupuesto,
    etaFromLogistics,
    legacyPoDate = null,
  ) => {
    const etaTimeline = phaseFromTimeline(timelineMap, "ETA");
    const poDate = legacyPoDate || getPresupuestoPoDate(presupuesto);
    return {
      PO: enrichFechaWithSemaforo(
        {
          date: poDate,
          source: "EXPECTED",
        },
        { ExpectedDate: poDate },
      ),
      PP_SEND: phaseFromTimeline(timelineMap, "PP send"),
      OK_PRODUCT: phaseFromTimeline(timelineMap, "Ok production Received"),
      TOP_APPROVED: phaseFromTimeline(timelineMap, "TOP approved"),
      FABRIC: phaseFromTimeline(timelineMap, "Fabrics and trims In-house"),
      PROD_START: phaseFromTimeline(timelineMap, "Production Starts"),
      INSP_DATE: phaseFromTimeline(timelineMap, "Inspection date"),
      ExFD: phaseFromTimeline(timelineMap, "ExFD"),
      ETD: phaseFromTimeline(timelineMap, "ETD"),
      DD: phaseFromTimeline(timelineMap, "DD"),
      ETA: etaTimeline?.date
        ? etaTimeline
        : etaFromLogistics
          ? enrichFechaWithSemaforo(
              { date: etaFromLogistics, source: "EXPECTED" },
              { ExpectedDate: etaFromLogistics },
            )
          : enrichFechaWithSemaforo({ date: null, source: null }, null),
    };
  };

  const applyLegacyPoToFechas = (fechas, legacyPoDate) => {
    if (!legacyPoDate || !fechas) return fechas;
    return {
      ...fechas,
      PO: enrichFechaWithSemaforo(
        { date: legacyPoDate, source: "EXPECTED" },
        { ExpectedDate: legacyPoDate },
      ),
    };
  };

  /** HasAdjuntos viene en cada nota desde GET /notas; evita N peticiones /adjuntos por entidad. */
  const notasIndicanAdjuntos = (notas) =>
    Array.isArray(notas) &&
    notas.some((n) => n.HasAdjuntos === true || Number(n.HasAdjuntos) === 1);

  // cargar todas las notas agrupadas por producto

  const muestrasByIdRef = useRef(null);
  const cpProdRowsRef = useRef(null);
  const cpProdRowsInflightRef = useRef(null);
  const cpRowByEntityKeyRef = useRef(new Map());
  const drawerEntityKeyRef = useRef(null);
  const buildFechasForBlockRef = useRef(null);
  const applyFechasRowRef = useRef(null);

  const formatHubFechaCell = (date) => {
    if (!date) return "—";
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format("DD/MM/YY") : "—";
  };

  const invalidateCpProdRowsCache = useCallback(() => {
    cpProdRowsRef.current = null;
    cpProdRowsInflightRef.current = null;
    cpRowByEntityKeyRef.current = new Map();
  }, []);

  const getCpProdRows = useCallback(async () => {
    if (cpProdRowsRef.current) return cpProdRowsRef.current;
    if (!cpProdRowsInflightRef.current) {
      cpProdRowsInflightRef.current = getCriticalPathHorizontal1(true)
        .then((prod) => {
          const rows = withLegacyLogisticsFlag(
            prodRowsExcludingMuestras(prod),
          );
          cpProdRowsRef.current = rows;
          return rows;
        })
        .catch((e) => {
          console.error("❌ Error cargando CP horizontal1 para hub", e);
          return [];
        })
        .finally(() => {
          cpProdRowsInflightRef.current = null;
        });
    }
    return cpProdRowsInflightRef.current;
  }, []);

  const resolveCpRowForBlock = useCallback(
    async (block) => {
      if (!block || block.idMuestra) return null;
      const cached = cpRowByEntityKeyRef.current.get(block.entityKey);
      if (cached !== undefined) return cached;
      try {
        const rows = await getCpProdRows();
        const row = findCpRowForHubBlock(block, rows) || null;
        cpRowByEntityKeyRef.current.set(block.entityKey, row);
        return row;
      } catch (e) {
        console.error("❌ Error resolviendo fila CP para hub", e);
        return null;
      }
    },
    [getCpProdRows],
  );

  const refreshLegacyPoFechaIfNeeded = useCallback(
    (entityKey) => {
      const block = groupedRef.current[entityKey];
      const legacyPoDate = block?.legacyPoDate;
      if (!legacyPoDate) return;

      const entry = fechasByKeyRef.current[entityKey];
      if (!entry?.loaded || !entry.fechas) return;
      if (poDatesMatch(entry.fechas?.PO?.date, legacyPoDate)) return;

      const nextFechas = applyLegacyPoToFechas(entry.fechas, legacyPoDate);
      setFechasByKey((prev) => {
        const next = {
          ...prev,
          [entityKey]: {
            fechas: nextFechas,
            loaded: true,
          },
        };
        fechasByKeyRef.current = next;
        patchHubCache({ fechasByKey: next });
        return next;
      });
    },
    [patchHubCache],
  );

  const patchBlockCpMeta = useCallback((entityKey, cpRow) => {
    if (!entityKey) return;
    const legacyPoDate = cpRow ? getLegacyPoDateFromRow(cpRow) : null;
    const hasLegacyLogistics = Boolean(cpRow?.HasLegacyLogistics);
    const prev = groupedRef.current;
    if (!prev[entityKey]) return;

    const next = {
      ...prev,
      [entityKey]: {
        ...prev[entityKey],
        legacyPoDate,
        hasLegacyLogistics,
        cpMetaLoaded: true,
      },
    };
    groupedRef.current = next;

    const cached = hubCacheRef.current.get(HUB_CACHE_ALL_KEY);
    if (cached?.loaded) {
      hubCacheRef.current.set(HUB_CACHE_ALL_KEY, {
        ...cached,
        grouped: { ...next },
      });
    }
    setGrouped(next);
    refreshLegacyPoFechaIfNeeded(entityKey);
  }, [refreshLegacyPoFechaIfNeeded]);

  const resolveCpRowForBlockSafe = useCallback(
    (block) => resolveCpRowForBlock(block).catch(() => null),
    [resolveCpRowForBlock],
  );

  const openCriticalPathFromBlock = useCallback(
    async (block) => {
      if (!block) return;
      drawerEntityKeyRef.current = block.entityKey;

      if (block.idMuestra && !block.idPresupuestoA3Pedido) {
        setDrawerMode("MUESTRA");
        setDrawerIdMuestra(block.idMuestra);
        setDrawerMuestraHeader(buildMuestraHeaderFromBlock(block));
        setDrawerProductId(null);
        setDrawerIdPresupuesto(null);
        setDrawerIdPresupuestoA3Pedido(null);
        setDrawerLegacyLogistics([]);
        setDrawerHasLegacyLogistics(false);
        setDrawerGridRowSnapshot(null);
        setDrawerOpen(true);
        return;
      }

      setDrawerMode("PRODUCCION");
      setDrawerIdMuestra(null);
      setDrawerMuestraHeader(null);
      setDrawerProductId(block.productId);
      setDrawerIdPresupuesto(
        block.idPresupuestoA3Pedido ? null : block.productId,
      );
      setDrawerIdPresupuestoA3Pedido(block.idPresupuestoA3Pedido ?? null);
      setDrawerLegacyLogistics([]);
      setDrawerHasLegacyLogistics(Boolean(block.hasLegacyLogistics));
      setDrawerGridRowSnapshot(null);
      setDrawerOpen(true);

      const cpRow = await resolveCpRowForBlockSafe(block);
      if (!cpRow?.ProductId) return;

      setDrawerProductId(cpRow.ProductId);
      setDrawerIdPresupuesto(resolveCpIdPresupuestoFromRow(cpRow));
      setDrawerIdPresupuestoA3Pedido(
        cpRow.IdPresupuestoA3Pedido != null &&
          Number.isFinite(Number(cpRow.IdPresupuestoA3Pedido))
          ? Number(cpRow.IdPresupuestoA3Pedido)
          : block.idPresupuestoA3Pedido ?? null,
      );
      setDrawerLegacyLogistics(extractLegacyLogistics(cpRow));
      setDrawerHasLegacyLogistics(Boolean(cpRow.HasLegacyLogistics));
      setDrawerGridRowSnapshot(cpRow);
      patchBlockCpMeta(block.entityKey, cpRow);

      if (isEntityFechasLoaded(fechasByKeyRef.current, block.entityKey)) {
        const refreshed = await buildFechasForBlock(
          groupedRef.current[block.entityKey],
        );
        if (refreshed) applyFechasRow(refreshed);
      }
    },
    [patchBlockCpMeta, resolveCpRowForBlockSafe],
  );

  const enrichHubBlocksLegacyFlags = useCallback(
    async (groupedMap, loadGen = hubLoadGenRef.current) => {
      try {
        const rows = await getCpProdRows();
        if (loadGen !== hubLoadGenRef.current) return;

        const patches = {};

        for (const [entityKey, block] of Object.entries(groupedMap)) {
          const cpRow = findCpRowForHubBlock(block, rows);
          if (!cpRow) continue;

          const hasLegacy = Boolean(cpRow.HasLegacyLogistics);
          const legacyPoDate = getLegacyPoDateFromRow(cpRow);
          const current = groupedRef.current[entityKey] || block;

          if (
            current.cpMetaLoaded &&
            current.hasLegacyLogistics === hasLegacy &&
            current.legacyPoDate === legacyPoDate
          ) {
            continue;
          }

          cpRowByEntityKeyRef.current.set(entityKey, cpRow);
          patches[entityKey] = {
            hasLegacyLogistics: hasLegacy,
            legacyPoDate,
            cpMetaLoaded: true,
          };
        }

        if (!Object.keys(patches).length) return;
        if (loadGen !== hubLoadGenRef.current) return;

        const nextGrouped = { ...groupedRef.current };
        for (const [entityKey, meta] of Object.entries(patches)) {
          if (!nextGrouped[entityKey]) continue;
          nextGrouped[entityKey] = { ...nextGrouped[entityKey], ...meta };
        }
        groupedRef.current = nextGrouped;

        const cached = hubCacheRef.current.get(HUB_CACHE_ALL_KEY);
        if (cached?.loaded) {
          hubCacheRef.current.set(HUB_CACHE_ALL_KEY, {
            ...cached,
            grouped: { ...nextGrouped },
          });
        }
        setGrouped(nextGrouped);

        for (const entityKey of Object.keys(patches)) {
          refreshLegacyPoFechaIfNeeded(entityKey);
        }
      } catch (e) {
        console.error("❌ Error marcando logística legacy en hub", e);
      }
    },
    [getCpProdRows, refreshLegacyPoFechaIfNeeded],
  );

  const prefetchLegacyMetaForHub = useCallback(
    (groupedMap, loadGen) => {
      void enrichHubBlocksLegacyFlags(groupedMap, loadGen);
    },
    [enrichHubBlocksLegacyFlags],
  );

  const getMuestraRow = async (idMuestra, { forceRefresh = false } = {}) => {
    if (forceRefresh) {
      muestrasByIdRef.current = null;
    }
    if (!muestrasByIdRef.current) {
      const raw = await getCriticalPathMuestras();
      const grouped = groupMuestras(raw);
      muestrasByIdRef.current = new Map(
        grouped.map((row) => [Number(row.ID_MUESTRA), row]),
      );
    }
    const id = Number(idMuestra);
    return (
      muestrasByIdRef.current.get(id) ||
      muestrasByIdRef.current.get(idMuestra) ||
      null
    );
  };

  const mergeProductInfoFromPresupuesto = (productInfo, presupuesto) => {
    if (!presupuesto) return productInfo || {};
    const base = productInfo || {};
    return {
      Cliente:
        base.Cliente ||
        presupuesto.ClienteNombre ||
        presupuesto.NombrePresupuesto ||
        null,
      Proveedor: base.Proveedor || presupuesto.ProveedorNombre || null,
      Temporada: base.Temporada || presupuesto.TemporadaNombre || null,
      Design:
        base.Design ||
        presupuesto.designCliente ||
        presupuesto.DiseñoMuestra ||
        null,
      NumeroPresupuesto:
        base.NumeroPresupuesto || presupuesto.NumeroPresupuesto || null,
    };
  };

  const blockNeedsProductInfo = (block) =>
    !block?.productInfo?.Design &&
    !block?.productInfo?.NumeroPresupuesto &&
    !block?.productInfo?.Cliente;

  const buildFechasForBlock = async (block) => {
    if (block.idPresupuestoA3Pedido) {
      try {
        const timelineOpts = {
          idPresupuestoA3Pedido: block.idPresupuestoA3Pedido,
        };
        const presupuestoPromise = blockNeedsProductInfo(block)
          ? getPresupuestoInfoByProductId(block.productId || 0, timelineOpts)
          : Promise.resolve(null);
        const [timeline, presupuesto] = await Promise.all([
          getTimelineByProductId(block.productId || 0, timelineOpts),
          presupuestoPromise,
        ]);
        const legacyPoDate = block.legacyPoDate ?? null;
        const timelineMap = {};
        timeline.forEach((t) => {
          timelineMap[t.TaskName] = t;
        });
        return {
          entityKey: block.entityKey,
          productInfo: mergeProductInfoFromPresupuesto(
            block.productInfo,
            presupuesto,
          ),
          fechas: buildProductionFechas(
            timelineMap,
            presupuesto,
            block.etaFromLogistics,
            legacyPoDate,
          ),
          hasLegacyLogistics: Boolean(block.hasLegacyLogistics),
        };
      } catch (e) {
        console.error("❌ Error cargando fechas pedido", block.entityKey, e);
        return null;
      }
    }

    if (block.productId && !block.idMuestra) {
      try {
        const timelineOpts = getProductionTimelineOpts(block);
        const presupuestoPromise = blockNeedsProductInfo(block)
          ? getPresupuestoInfoByProductId(block.productId, timelineOpts)
          : Promise.resolve(null);
        const [timeline, presupuesto] = await Promise.all([
          getTimelineByProductId(block.productId, timelineOpts),
          presupuestoPromise,
        ]);

        const productInfo = mergeProductInfoFromPresupuesto(
          block.productInfo,
          presupuesto,
        );

        const legacyPoDate = block.legacyPoDate ?? null;
        const timelineMap = {};
        timeline.forEach((t) => {
          timelineMap[t.TaskName] = t;
        });

        return {
          entityKey: block.entityKey,
          productInfo,
          fechas: buildProductionFechas(
            timelineMap,
            presupuesto,
            block.etaFromLogistics,
            legacyPoDate,
          ),
          hasLegacyLogistics: Boolean(block.hasLegacyLogistics),
        };
      } catch (e) {
        console.error("❌ Error cargando fechas", block.entityKey, e);
        return null;
      }
    }

    if (block.idMuestra) {
      try {
        const muestra = await getMuestraRow(block.idMuestra);
        if (!muestra) return null;

        const etaTimeline = phaseFromMuestraTask(muestra, "ETA");

        return {
          entityKey: block.entityKey,
          fechas: {
            PP_SEND: phaseFromMuestraTask(muestra, "PP send"),
            OK_PRODUCT: phaseFromMuestraTask(
              muestra,
              "Ok production Received",
            ),
            TOP_APPROVED: phaseFromMuestraTask(muestra, "TOP approved"),
            FABRIC: phaseFromMuestraTask(
              muestra,
              "Fabrics and trims In-house",
            ),
            PROD_START: phaseFromMuestraTask(muestra, "Production Starts"),
            INSP_DATE: phaseFromMuestraTask(muestra, "Inspection date"),
            ExFD: phaseFromMuestraTask(muestra, "ExFD"),
            ETD: phaseFromMuestraTask(muestra, "ETD"),
            DD: phaseFromMuestraTask(muestra, "DD"),
            ETA: etaTimeline?.date
              ? etaTimeline
              : block.etaFromLogistics
                ? enrichFechaWithSemaforo(
                    { date: block.etaFromLogistics, source: "EXPECTED" },
                    { ExpectedDate: block.etaFromLogistics },
                  )
                : enrichFechaWithSemaforo({ date: null, source: null }, null),
          },
        };
      } catch (e) {
        console.error("❌ Error cargando fechas", block.entityKey, e);
        return null;
      }
    }

    return null;
  };

  useEffect(() => {
    groupedRef.current = grouped;
  }, [grouped]);

  useEffect(() => {
    fechasByKeyRef.current = fechasByKey;
  }, [fechasByKey]);

  const applyFechasRow = useCallback((row) => {
    if (!row?.entityKey) return;
    const block = groupedRef.current[row.entityKey];
    const fechas = block?.legacyPoDate
      ? applyLegacyPoToFechas(row.fechas, block.legacyPoDate)
      : row.fechas;
    setFechasByKey((prev) => {
      const next = {
        ...prev,
        [row.entityKey]: {
          fechas,
          loaded: true,
        },
      };
      fechasByKeyRef.current = next;
      patchHubCache({ fechasByKey: next });
      return next;
    });
    if (row.productInfo) {
      setGrouped((prev) => {
        if (!prev[row.entityKey]) return prev;
        const next = {
          ...prev,
          [row.entityKey]: {
            ...prev[row.entityKey],
            productInfo: {
              ...prev[row.entityKey].productInfo,
              ...row.productInfo,
            },
            ...(row.hasLegacyLogistics != null
              ? { hasLegacyLogistics: row.hasLegacyLogistics }
              : {}),
          },
        };
        groupedRef.current = next;
        return next;
      });
    }
  }, [patchHubCache]);

  buildFechasForBlockRef.current = buildFechasForBlock;
  applyFechasRowRef.current = applyFechasRow;

  const markFechasLoaded = useCallback((entityKey) => {
    setFechasByKey((prev) => {
      if (prev[entityKey]?.loaded) return prev;
      const next = {
        ...prev,
        [entityKey]: {
          fechas: prev[entityKey]?.fechas || EMPTY_FECHAS_PLACEHOLDER,
          loaded: true,
        },
      };
      fechasByKeyRef.current = next;
      return next;
    });
  }, []);

  const blockToBulkItem = (block) => ({
    entityKey: block.entityKey,
    productId: block.productId ?? null,
    idPresupuesto:
      block.productId && !block.idMuestra && !block.idPresupuestoA3Pedido
        ? block.productId
        : null,
    idPresupuestoA3Pedido: block.idPresupuestoA3Pedido ?? null,
    idMuestra: block.idMuestra ?? null,
  });

  const flushFechasQueue = async () => {
    if (fechasFlushRunningRef.current) return;
    fechasFlushRunningRef.current = true;

    try {
      while (fechasQueueRef.current.size > 0) {
        const batchKeys = [...fechasQueueRef.current].slice(
          0,
          NOTAS_RESUMEN_FECHAS_BULK_SIZE,
        );
        for (const key of batchKeys) {
          fechasQueueRef.current.delete(key);
        }

        const blocks = batchKeys
          .map((key) => groupedRef.current[key])
          .filter(
            (block) =>
              block &&
              !isEntityFechasLoaded(fechasByKeyRef.current, block.entityKey) &&
              !fechasInflightRef.current.has(block.entityKey),
          );

        if (!blocks.length) continue;

        for (const block of blocks) {
          fechasInflightRef.current.add(block.entityKey);
        }

        const muestraBlocks = blocks.filter((b) => b.idMuestra);
        const timelineBlocks = blocks.filter((b) => !b.idMuestra);

        try {
          if (timelineBlocks.length) {
            let bulkHandled = false;

            try {
              const bulkResult = await getTimelinesBulkForHub(
                timelineBlocks.map(blockToBulkItem),
              );

              if (bulkResult) {
                bulkHandled = true;
                for (const item of bulkResult.items || []) {
                  const block = groupedRef.current[item.entityKey];
                  if (!block || !(item.timeline || []).length) continue;

                  const timelineMap = {};
                  (item.timeline || []).forEach((task) => {
                    timelineMap[task.TaskName] = task;
                  });

                  applyFechasRow({
                    entityKey: item.entityKey,
                    productInfo: mergeProductInfoFromPresupuesto(
                      block.productInfo,
                      item.presupuesto,
                    ),
                    fechas: buildProductionFechas(
                      timelineMap,
                      item.presupuesto,
                      block.etaFromLogistics,
                      block.legacyPoDate ?? null,
                    ),
                  });
                }
              }
            } catch (bulkError) {
              console.error("❌ Error cargando fechas bulk", bulkError);
            }

            const pendingTimelineBlocks = timelineBlocks.filter(
              (block) =>
                !isEntityFechasLoaded(
                  fechasByKeyRef.current,
                  block.entityKey,
                ),
            );

            if (!bulkHandled || pendingTimelineBlocks.length) {
              const rows = await mapPool(
                pendingTimelineBlocks.length
                  ? pendingTimelineBlocks
                  : timelineBlocks,
                6,
                (block) => buildFechasForBlock(block),
              );
              for (const row of rows) {
                if (row) applyFechasRow(row);
              }
            }

            for (const block of timelineBlocks) {
              if (
                !isEntityFechasLoaded(
                  fechasByKeyRef.current,
                  block.entityKey,
                )
              ) {
                markFechasLoaded(block.entityKey);
              }
            }
          }

          if (muestraBlocks.length) {
            const rows = await mapPool(muestraBlocks, 4, (block) =>
              buildFechasForBlock(block),
            );
            for (const row of rows) {
              if (row) {
                applyFechasRow(row);
              }
            }
            for (const block of muestraBlocks) {
              if (
                !isEntityFechasLoaded(
                  fechasByKeyRef.current,
                  block.entityKey,
                )
              ) {
                markFechasLoaded(block.entityKey);
              }
            }
          }
        } catch (e) {
          console.error("❌ Error cargando fechas", e);
          const rows = await mapPool(blocks, 6, (block) =>
            buildFechasForBlock(block),
          );
          for (const row of rows) {
            if (row) applyFechasRow(row);
          }
          for (const block of blocks) {
            if (
              !isEntityFechasLoaded(
                fechasByKeyRef.current,
                block.entityKey,
              )
            ) {
              markFechasLoaded(block.entityKey);
            }
          }
        } finally {
          for (const block of blocks) {
            fechasInflightRef.current.delete(block.entityKey);
          }
        }
      }
    } finally {
      fechasFlushRunningRef.current = false;
      if (fechasQueueRef.current.size > 0 && !fechasFlushTimerRef.current) {
        fechasFlushTimerRef.current = setTimeout(() => {
          fechasFlushTimerRef.current = null;
          flushFechasQueueRef.current?.();
        }, 60);
      }
    }
  };

  flushFechasQueueRef.current = flushFechasQueue;

  const requestFechasLoad = useCallback((entityKey, { immediate = false } = {}) => {
    const block = groupedRef.current[entityKey];
    if (
      !block ||
      isEntityFechasLoaded(fechasByKeyRef.current, entityKey) ||
      fechasInflightRef.current.has(entityKey)
    ) {
      return;
    }

    fechasQueueRef.current.add(entityKey);
    if (immediate) {
      if (fechasFlushTimerRef.current) {
        clearTimeout(fechasFlushTimerRef.current);
        fechasFlushTimerRef.current = null;
      }
      flushFechasQueueRef.current?.();
      return;
    }

    if (!fechasFlushTimerRef.current) {
      fechasFlushTimerRef.current = setTimeout(() => {
        fechasFlushTimerRef.current = null;
        flushFechasQueueRef.current?.();
      }, 60);
    }
  }, []);

  const loadFechasForEntity = async (entityKey) => {
    requestFechasLoad(entityKey);
  };

  const loadNotasResumen = async (userId, loadGen) => {
    if (!userId) return;

    setHubLoading(true);
    setHubLoadError(null);

    const isStale = () => loadGen !== hubLoadGenRef.current;

    try {
      const { blocks } = await getNotasResumenWithFallback({
        userId,
        entityType: "PRODUCT",
        includeClosed: "all",
      });

      if (isStale()) return;

      const attachmentsSet = new Set();
      const groupedMap = {};
      const clientesFromBlocks = new Set();

      for (const block of blocks || []) {
        const blockNotas = block.notas || [];
        const notesFromApi = blockNotas.length > 0;
        groupedMap[block.entityKey] = {
          ...block,
          notas: notesFromApi ? blockNotas : [],
          notasLoaded: notesFromApi,
          notasLoading: false,
          noteCount: Number(block.noteCount) || blockNotas.length || 0,
          openIssueCount:
            Number(block.openIssueCount) ||
            countOpenIssuesInNotas(blockNotas),
        };
        if (block.hasAttachments) {
          attachmentsSet.add(block.entityKey);
        }
        const clienteName = getBlockCliente(block);
        if (clienteName && clienteName !== "—") {
          clientesFromBlocks.add(clienteName);
        }
      }

      setFechasByKey({});
      fechasByKeyRef.current = {};
      setGrouped(groupedMap);
      setProductsWithAttachments(attachmentsSet);
      groupedRef.current = groupedMap;

      if (clientesFromBlocks.size) {
        setHubClientes((prev) => {
          const merged = new Set([...(prev || []), ...clientesFromBlocks]);
          return Array.from(merged).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" }),
          );
        });
      }

      hubCacheRef.current.set(HUB_CACHE_ALL_KEY, {
        grouped: { ...groupedMap },
        fechasByKey: {},
        attachments: [...attachmentsSet],
        loaded: true,
      });

      // Progressive legacy flags (skipped on backend for full hub)
      prefetchLegacyMetaForHub(groupedMap, loadGen);
    } catch (e) {
      if (isStale()) return;
      console.error("❌ Error cargando resumen notas", e);
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        "Error desconocido";
      setHubLoadError(String(detail));
      setGrouped({});
      setFechasByKey({});
      fechasByKeyRef.current = {};
      setProductsWithAttachments(new Set());
    } finally {
      if (loadGen === hubLoadGenRef.current) {
        setHubLoading(false);
      }
    }
  };

  const refreshSingleProduct = async (productId) => {
    const entityKey = `P_${productId}`;

    try {
      // 1️⃣ Volvemos a pedir el producto actualizado
      const products = await getCriticalPathHorizontal();
      const product = products.find((p) => p.ProductId === productId);

      // Si el producto ya no existe → lo quitamos
      if (!product) {
        setGrouped((prev) => {
          const copy = { ...prev };
          delete copy[entityKey];
          return copy;
        });
        return;
      }

      // 2️⃣ Regla de negocio: OriginalTelexDocsSemt
      const hasOriginalTelexSent =
        Array.isArray(product.logistics) &&
        product.logistics.some((l) => l.OriginalTelexDocsSemt !== null);

      if (hasOriginalTelexSent) {
        setGrouped((prev) => {
          const copy = { ...prev };
          delete copy[entityKey];
          return copy;
        });
        return;
      }

      // 3️⃣ Notas actualizadas
      const token = localStorage.getItem("token");
      const { userId } = jwtDecode(token);

      const notas = await getNotasByEntityWithUser({
        productId,
        userId,
      });

      const hasAttachments = notasIndicanAdjuntos(notas);

      // 4️⃣ Timeline + presupuesto
      const [timeline, presupuesto] = await Promise.all([
        getTimelineByProductId(productId),
        getPresupuestoInfoByProductId(productId),
      ]);

      const timelineMap = {};
      timeline.forEach((t) => {
        timelineMap[t.TaskName] = t;
      });

      const etaTimeline = getBestDate(timelineMap["ETA"]);
      const etaFromLogistics =
        product.logistics?.find((l) => l.ETA)?.ETA || null;

      const productionFechas = {
        PO: {
          date: presupuesto?.FechaAceptacion || null,
          source: "EXPECTED",
        },
        PP_SEND: getBestDate(timelineMap["PP send"]),
        OK_PRODUCT: getBestDate(timelineMap["Ok production Received"]),
        TOP_APPROVED: getBestDate(timelineMap["TOP approved"]),
        FABRIC: getBestDate(timelineMap["Fabrics and trims In-house"]),
        PROD_START: getBestDate(timelineMap["Production Starts"]),
        INSP_DATE: getBestDate(timelineMap["Inspection date"]),
        ExFD: getBestDate(timelineMap["ExFD"]),
        ETD: getBestDate(timelineMap["ETD"]),
        DD: getBestDate(timelineMap["DD"]),
        ETA: etaTimeline?.date
          ? etaTimeline
          : etaFromLogistics
            ? { date: etaFromLogistics, source: "EXPECTED" }
            : { date: null, source: null },
      };

      setGrouped((prev) => {
        const existing = prev[entityKey] || {};
        const next = {
          ...existing,
          entityKey,
          productId,
          idMuestra: null,
          hasAttachments,
          etaFromLogistics,
          productInfo: {
            ...existing.productInfo,
            Cliente: product.MarcaNombre || product.CLiente,
            Proveedor: product.ProveedorNombre,
            Temporada: product.TemporadaNombre,
            Design: product.designCliente,
            NumeroPresupuesto: product.NumeroPresupuesto,
          },
          notas: notas || [],
          notasLoaded: true,
          notasLoading: false,
          noteCount: (notas || []).length,
          openIssueCount: countOpenIssuesInNotas(notas),
        };
        groupedRef.current = { ...prev, [entityKey]: next };
        return { ...prev, [entityKey]: next };
      });

      applyFechasRow({ entityKey, fechas: productionFechas });

      setProductsWithAttachments((prev) => {
        const next = new Set(prev);
        if (hasAttachments) next.add(entityKey);
        else next.delete(entityKey);
        return next;
      });
    } catch (e) {
      console.error("❌ Error refrescando producto", productId, e);
    }
  };

  const refreshSingleMuestra = async (idMuestra) => {
    const entityKey = `M_${idMuestra}`;

    try {
      const token = localStorage.getItem("token");
      const { userId } = jwtDecode(token);

      const muestra = await getMuestraRow(idMuestra, { forceRefresh: true });

      if (!muestra) {
        setGrouped((prev) => {
          const copy = { ...prev };
          delete copy[entityKey];
          return copy;
        });
        return;
      }

      const notas = await getNotasByEntityWithUser({
        idMuestra,
        userId,
      });

      const block = groupedRef.current[entityKey];
      if (!block) return;

      const etaTimeline = phaseFromMuestraTask(muestra, "ETA");

      const muestraFechas = {
        PP_SEND: phaseFromMuestraTask(muestra, "PP send"),
        OK_PRODUCT: phaseFromMuestraTask(muestra, "Ok production Received"),
        TOP_APPROVED: phaseFromMuestraTask(muestra, "TOP approved"),
        FABRIC: phaseFromMuestraTask(muestra, "Fabrics and trims In-house"),
        PROD_START: phaseFromMuestraTask(muestra, "Production Starts"),
        INSP_DATE: phaseFromMuestraTask(muestra, "Inspection date"),
        ExFD: phaseFromMuestraTask(muestra, "ExFD"),
        ETD: phaseFromMuestraTask(muestra, "ETD"),
        DD: phaseFromMuestraTask(muestra, "DD"),
        ETA: etaTimeline?.date
          ? etaTimeline
          : block?.etaFromLogistics
            ? enrichFechaWithSemaforo(
                { date: block.etaFromLogistics, source: "EXPECTED" },
                { ExpectedDate: block.etaFromLogistics },
              )
            : enrichFechaWithSemaforo({ date: null, source: null }, null),
      };

      const hasAttachments = notasIndicanAdjuntos(notas);

      setGrouped((prev) => {
        if (!prev[entityKey]) return prev;
        const next = {
          ...prev[entityKey],
          notas: notas || [],
          notasLoaded: true,
          notasLoading: false,
          noteCount: (notas || []).length,
          openIssueCount: countOpenIssuesInNotas(notas),
          hasAttachments: hasAttachments || prev[entityKey].hasAttachments,
          productInfo: {
            ...prev[entityKey].productInfo,
            Design:
              prev[entityKey].productInfo?.Design ||
              muestra.DiseñoMuestra ||
              muestra.NombreEntidad,
            Cliente:
              prev[entityKey].productInfo?.Cliente || muestra.MarcaNombre,
            Proveedor:
              prev[entityKey].productInfo?.Proveedor || muestra.ProveedorNombre,
          },
        };
        groupedRef.current = { ...prev, [entityKey]: next };
        return { ...prev, [entityKey]: next };
      });

      applyFechasRow({ entityKey, fechas: muestraFechas });
    } catch (err) {
      console.error("❌ Error refrescando muestra", idMuestra, err);
    }
  };

  const unreadCountByEntity = (entityKey) => {
    const block = grouped[entityKey];
    if (!block) return 0;

    if (block.notasLoaded && Array.isArray(block.notas)) {
      return block.notas.filter((n) => unreadSnapshot.has(n.Id)).length;
    }

    return Number(unreadCountByEntityKey[entityKey]) || 0;
  };

  const openAttachmentsDialog = async (entityKey) => {
    const block = grouped[entityKey];
    if (!block) return;

    const token = localStorage.getItem("token");
    const adjuntosLists = await mapPool(
      block.notas,
      NOTAS_RESUMEN_ADJUNTOS_CONCURRENCY,
      async (n) => {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/criticalpath/notas-files/${n.Id}/adjuntos`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        return res.json();
      },
    );

    const allFiles = [];
    for (const data of adjuntosLists) {
      if (Array.isArray(data) && data.length) {
        allFiles.push(...data);
      }
    }

    if (allFiles.length === 0) {
      return; // no hay adjuntos → no abrimos nada
    }

    setAttachments(allFiles);
    setAttachmentsDialogOpen(true);
  };

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem("token");
      if (!token) return navigate("/login");

      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;

        if (decoded.exp < now) {
          localStorage.removeItem("token");
          return navigate("/login");
        }

        userIdRef.current = decoded.userId;
        setClientsLoading(true);
        setHubLoadError(null);
        const loadGen = ++hubLoadGenRef.current;

        void loadNotasResumen(decoded.userId, loadGen);

        try {
          const { clientes = [] } = await getNotasResumenClientes({
            userId: decoded.userId,
          });
          setHubClientes(clientes);
        } catch (err) {
          console.error("Error loading hub clients:", err);
        } finally {
          setClientsLoading(false);
        }
      } catch (e) {
        console.error("Error loading user:", e);
        navigate("/login");
        setClientsLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const resetClientScopedFilters = useCallback(() => {
    setSearch("");
    setProveedorFilter("ALL");
    setPresupuestoEstadoFilter("ALL");
  }, []);

  const handleHubClienteChange = (event) => {
    const nextCliente = String(event.target.value || "").trim();
    setHubCliente(nextCliente);
    setOpenCards({});
    resetClientScopedFilters();
  };

  const handleToggleShowClosed = () => {
    setShowClosedEntities((prev) => !prev);
    setOpenCards({});
  };

  const applyFilterValue = useCallback((setter) => {
    return (event) => {
      setter(event.target.value);
    };
  }, []);

  const handleToggleEntityClosed = async (block) => {
    if (!block) return;
    const willClose = !Boolean(block.isClosed);
    try {
      await setCriticalPathEntityClosed({
        productId: block.idPresupuestoA3Pedido ? null : block.productId,
        idMuestra: block.idMuestra,
        idPresupuestoA3Pedido: block.idPresupuestoA3Pedido,
        isClosed: willClose,
      });
      setGrouped((prev) => {
        const next = { ...prev };
        if (willClose && !showClosedEntities) {
          delete next[block.entityKey];
          return next;
        }
        if (!willClose && showClosedEntities) {
          delete next[block.entityKey];
          return next;
        }
        next[block.entityKey] = {
          ...next[block.entityKey],
          isClosed: willClose,
        };
        return next;
      });
    } catch (e) {
      console.error("❌ Error cerrando entidad hub", e);
    }
  };

  const handleRequestToggleEntityClosed = (block) => {
    if (!block) return;
    const willClose = !Boolean(block.isClosed);
    if (!willClose) {
      handleToggleEntityClosed(block);
      return;
    }
    setCloseConfirmState({ open: true, block });
  };

  const handleConfirmCloseEntity = async () => {
    const blockToClose = closeConfirmState.block;
    setCloseConfirmState({ open: false, block: null });
    if (!blockToClose) return;
    await handleToggleEntityClosed(blockToClose);
  };

  const filterFieldSx = {
    width: "100%",
    minWidth: 0,
    "& .MuiInputBase-root": {
      backgroundColor: alpha(theme.palette.background.paper, 0.98),
      borderRadius: 1.25,
      fontSize: "0.8125rem",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.8125rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.divider, 0.72),
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.primary.main, 0.45),
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  };

  const inlineSearchSx = {
    ...filterFieldSx,
    width: { xs: "100%", sm: 200, md: 240 },
    flexShrink: 0,
    "& .MuiInputBase-root": {
      backgroundColor: alpha(theme.palette.background.paper, 0.98),
      borderRadius: 1.25,
      fontSize: "0.8125rem",
      height: 36,
    },
  };

  const selectMenuProps = {
    disableScrollLock: true,
    PaperProps: {
      elevation: 8,
      sx: {
        mt: 0.5,
        borderRadius: 2,
        maxHeight: 320,
        border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        "& .MuiMenuItem-root": {
          fontSize: "0.8125rem",
          py: 0.875,
          borderRadius: 1,
          mx: 0.5,
          my: 0.25,
          "&.Mui-selected": {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            fontWeight: 700,
          },
          "&.Mui-selected:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.18),
          },
        },
      },
    },
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  const toggleChipSx = (active, paletteKey = "primary") => ({
    height: 28,
    fontWeight: 700,
    fontSize: "0.7rem",
    borderRadius: 999,
    px: 0.25,
    transition: "all 0.15s ease",
    ...(active
      ? {
          bgcolor: alpha(theme.palette[paletteKey].main, 0.14),
          color: theme.palette[paletteKey].dark,
          border: "1px solid",
          borderColor: alpha(theme.palette[paletteKey].main, 0.35),
        }
      : {
          bgcolor: "transparent",
          color: theme.palette.text.secondary,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.85),
        }),
  });

  const phaseSemaforoColor = (value) =>
    muiColorFromSemaforo(value?.semaforo, theme);

  const refreshEntityNotas = async (entityKey) => {
    if (!entityKey) return;

    const block = grouped[entityKey];
    if (!block) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    const { userId } = jwtDecode(token);

    try {
      const notas = await getNotasByEntityWithUser({
        productId: block.productId,
        idMuestra: block.idMuestra,
        idPresupuestoA3Pedido: block.idPresupuestoA3Pedido,
        userId,
      });

      setGrouped((prev) => {
        const copy = { ...prev };
        copy[entityKey] = {
          ...copy[entityKey],
          notas: notas || [],
        };
        return copy;
      });
    } catch (err) {
      console.error("❌ Error refreshing entity notes:", err);
    }
  };

  // aplicar filtros
  const proveedorOptions = useMemo(() => {
    const names = new Set();
    Object.values(grouped).forEach((block) => {
      const name = getBlockProveedor(block);
      if (name && name !== "—") names.add(name);
    });
    return Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [grouped]);

  const fechasForAlertFilter = onlyAlert ? fechasByKey : null;

  const filteredBlocks = useMemo(() => {
    const searchLower = deferredSearch.trim().toLowerCase();
    const noteFiltersActive =
      onlyOpenIssues ||
      tipoNota !== "ALL" ||
      status !== "ALL" ||
      responsible !== "ALL";

    const noteFilterState = {
      onlyOpenIssues,
      tipoNota,
      status,
      responsible,
    };

    const result = [];

    for (const block of Object.values(grouped)) {
      if (!block?.entityKey) continue;

      if (showClosedEntities ? !block.isClosed : block.isClosed) continue;

      if (block.idMuestra) continue;

      if (
        hubCliente &&
        getBlockCliente(block) !== hubCliente
      ) {
        continue;
      }

      const displayNotas = noteFiltersActive
        ? filterBlockNotas(block.notas, noteFilterState)
        : block.notas || [];

      if (onlyWithNotes && !(block.noteCount > 0 || displayNotas.length)) continue;

      if (
        proveedorFilter !== "ALL" &&
        getBlockProveedor(block) !== proveedorFilter
      ) {
        continue;
      }

      if (
        presupuestoEstadoFilter !== "ALL" &&
        getBlockPresupuestoEstado(block) !== presupuestoEstadoFilter
      ) {
        continue;
      }

      if (onlyAlert) {
        const fechasEntry = fechasForAlertFilter?.[block.entityKey];
        if (!fechasEntry?.loaded) continue;
        const score = scoreBlockSemaforo(fechasEntry.fechas);
        if (score < semaforoLevelWeight(SEMAFORO_LEVELS.ORANGE)) continue;
      }

      if (noteFiltersActive && !displayNotas.length && !(block.noteCount > 0)) {
        continue;
      }

      if (searchLower) {
        const clienteMatch =
          block.productInfo?.Cliente?.toLowerCase().includes(searchLower);
        const proveedorMatch =
          block.productInfo?.Proveedor?.toLowerCase().includes(searchLower);
        const designMatch =
          block.productInfo?.Design?.toLowerCase().includes(searchLower) ||
          block.productInfo?.NumeroPresupuesto?.toLowerCase().includes(
            searchLower,
          );
        const notaMatch = displayNotas.some((n) =>
          n.Nota?.toLowerCase().includes(searchLower),
        );
        if (
          !clienteMatch &&
          !proveedorMatch &&
          !designMatch &&
          !notaMatch
        ) {
          continue;
        }
      }

      result.push({
        entityKey: block.entityKey,
        displayNotas: noteFiltersActive ? displayNotas : null,
      });
    }

    result.sort((a, b) =>
      compareBlocksByCliente(grouped[a.entityKey], grouped[b.entityKey]),
    );

    return result;
  }, [
    grouped,
    fechasForAlertFilter,
    tipoNota,
    status,
    responsible,
    onlyOpenIssues,
    deferredSearch,
    proveedorFilter,
    onlyWithNotes,
    onlyAlert,
    presupuestoEstadoFilter,
    showClosedEntities,
    hubCliente,
  ]);

  // Badge notas en el sidebar

  // Métricas para el hero (totales sobre los resultados filtrados)
  const heroStats = useMemo(() => {
    let totalNotes = 0;
    let openIssues = 0;
    filteredBlocks.forEach(({ entityKey, displayNotas }) => {
      const block = grouped[entityKey];
      const notas = displayNotas ?? block?.notas ?? [];
      const noteCount = notas.length || Number(block?.noteCount) || 0;
      totalNotes += noteCount;
      notas.forEach((n) => {
        if (n.TipoNota === "Open Issue" && n.Status !== "Closed") {
          openIssues += 1;
        }
      });
    });
    return {
      entityCount: filteredBlocks.length,
      totalNotes,
      openIssues,
    };
  }, [filteredBlocks, grouped]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (proveedorFilter !== "ALL") count += 1;
    if (presupuestoEstadoFilter !== "ALL") count += 1;
    if (tipoNota !== "ALL") count += 1;
    if (status !== "ALL") count += 1;
    if (responsible !== "ALL") count += 1;
    return count;
  }, [
    proveedorFilter,
    presupuestoEstadoFilter,
    tipoNota,
    status,
    responsible,
  ]);

  const allFiltersLabel = t("Notes.Todos", { defaultValue: "Todos" });

  const isFilterActive = (value) => value !== "ALL";

  const legendItems = useMemo(
    () => [
      {
        color: theme.palette.success.main,
        label: t("NotasResumenPage.legendOk", { defaultValue: "OK / Real" }),
      },
      {
        color: theme.palette.warning.main,
        label: t("NotasResumenPage.legendAttention", {
          defaultValue: "≤10 días",
        }),
      },
      {
        color: theme.palette.error.main,
        label: t("NotasResumenPage.legendUrgent", { defaultValue: "≤5 días" }),
      },
      {
        color: theme.palette.text.disabled,
        label: t("NotasResumenPage.legendOverdue", {
          defaultValue: "Vencido",
        }),
      },
      {
        color: theme.palette.info.main,
        label: t("NotasResumenPage.legendAttachments"),
      },
    ],
    [t, theme],
  );

  const clearAdvancedFilters = () => {
    setProveedorFilter("ALL");
    setPresupuestoEstadoFilter("ALL");
    setTipoNota("ALL");
    setStatus("ALL");
    setResponsible("ALL");
  };

  const NotasResumenSkeleton = () => (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        px: 2,
        py: 1.5,
      }}
    >
      {/* Cabecera */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton variant="rectangular" width={120} height={18} />
        <Skeleton variant="text" width="40%" />
      </Box>

      {/* Subtítulo */}
      <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />

      {/* Fechas */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: 1,
          mt: 2,
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={42}
            sx={{ borderRadius: 1 }}
          />
        ))}
      </Box>
    </Paper>
  );

  // Padding lateral común para cabecera y listado scroll.
  const sectionPxSx = { px: { xs: 1.5, md: 3 } };

  const pageShellSx = {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    maxHeight: "100dvh",
    overflow: "hidden",
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.default,
    ...contentMargin,
  };

  const pageHeaderSx = {
    ...sectionPxSx,
    pt: { xs: 1, md: 1.25 },
    pb: { xs: 0.75, md: 0.75 },
    flexShrink: 0,
    backgroundColor: alpha(theme.palette.background.default, 0.96),
    backdropFilter: "blur(8px)",
    borderBottom: `1px solid ${theme.palette.divider}`,
    zIndex: 9,
  };

  const pageScrollSx = {
    ...sectionPxSx,
    pt: 1,
    pb: 3,
    pr: { xs: 0.75, md: 1.25 },
    flex: 1,
    minHeight: 0,
    overflowY: "scroll",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor: `${alpha(theme.palette.text.secondary, 0.55)} ${alpha(
      theme.palette.divider,
      theme.palette.mode === "dark" ? 0.35 : 0.5,
    )}`,
    "&::-webkit-scrollbar": {
      width: 12,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: alpha(
        theme.palette.divider,
        theme.palette.mode === "dark" ? 0.35 : 0.45,
      ),
      borderRadius: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: alpha(theme.palette.text.secondary, 0.5),
      borderRadius: 6,
      border: `2px solid ${alpha(theme.palette.background.default, 0.85)}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: alpha(theme.palette.text.secondary, 0.72),
    },
  };

  return (
    <Box sx={pageShellSx}>
      {/* ============== CABECERA FIJA (filtros + leyenda) ============== */}
      <Box sx={pageHeaderSx}>
        <Box sx={{ ...pygHeroShellSx(theme), mb: 0 }}>
          <Box sx={{ px: { xs: 1.25, md: 1.5 }, py: { xs: 1, md: 1.1 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
              spacing={{ xs: 1, md: 1.25 }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ minWidth: 0, flex: "1 1 auto" }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                    color: theme.palette.primary.dark,
                  }}
                >
                  <EditNoteOutlinedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.15,
                      fontSize: { xs: "1.05rem", md: "1.12rem" },
                    }}
                  >
                    {t("sidebar.criticalPathNotes")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 0.35, rowGap: 0.35 }}
                  >
                    <Chip
                      label={t("NotasResumenPage.productsCount", {
                        count: heroStats.entityCount,
                        defaultValue: "{{count}} items",
                      })}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
                    />
                    {heroStats.totalNotes > 0 ? (
                      <Chip
                        label={`${heroStats.totalNotes} ${t("Notes.Notes", {
                          defaultValue: "notas",
                        })}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.dark,
                        }}
                      />
                    ) : null}
                    {heroStats.openIssues > 0 ? (
                      <Chip
                        label={`${heroStats.openIssues} ${t("Notes.Open issue", {
                          defaultValue: "open issues",
                        })}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.error.main, 0.1),
                          color: theme.palette.error.dark,
                        }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{
                  flexShrink: 0,
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  rowGap: 0.5,
                }}
              >
                <Select
                  size="small"
                  displayEmpty
                  value={hubCliente}
                  onChange={handleHubClienteChange}
                  disabled={clientsLoading && !hubClientes.length}
                  sx={{
                    minWidth: { xs: "100%", md: 220 },
                    maxWidth: { xs: "100%", md: 280 },
                    ...inlineSearchSx,
                    "& .MuiInputBase-root": {
                      ...inlineSearchSx["& .MuiInputBase-root"],
                      height: 36,
                    },
                  }}
                  renderValue={(selected) => {
                    if (clientsLoading && !hubClientes.length) {
                      return t("NotasResumenPage.loadingClients", {
                        defaultValue: "Cargando clientes…",
                      });
                    }
                    if (!selected) {
                      return t("NotasResumenPage.allClients", {
                        defaultValue: "Todos los clientes",
                      });
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em>
                      {t("NotasResumenPage.allClients", {
                        defaultValue: "Todos los clientes",
                      })}
                    </em>
                  </MenuItem>
                  {hubClientes.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>

                <TextField
                  size="small"
                  placeholder={t("NotasResumenPage.searchPlaceholder", {
                    defaultValue: "Buscar cliente, diseño o nota…",
                  })}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={inlineSearchSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Badge
                  color="primary"
                  badgeContent={activeFilterCount}
                  invisible={activeFilterCount === 0}
                  sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem" } }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FilterListOutlinedIcon />}
                    onClick={(e) => setFiltersAnchorEl(e.currentTarget)}
                    sx={{
                      height: 36,
                      px: 1.25,
                      fontWeight: 700,
                      borderRadius: 1.25,
                      whiteSpace: "nowrap",
                      borderColor: alpha(theme.palette.divider, 0.85),
                      bgcolor: alpha(theme.palette.background.paper, 0.92),
                    }}
                  >
                    {t("NotasResumenPage.filtersLabel", {
                      defaultValue: "Filtros",
                    })}
                  </Button>
                </Badge>

                <Tooltip
                  title={t("NotasResumenPage.onlyWithNotesHint", {
                    defaultValue:
                      "Solo líneas que tienen al menos una nota (el hub muestra también líneas CP sin notas).",
                  })}
                >
                  <Chip
                    size="small"
                    label={t("NotasResumenPage.onlyWithNotes", {
                      defaultValue: "Con notas",
                    })}
                    clickable
                    onClick={() => setOnlyWithNotes((v) => !v)}
                    sx={toggleChipSx(onlyWithNotes, "primary")}
                  />
                </Tooltip>
                <Tooltip
                  title={t("NotasResumenPage.onlyAlertHint", {
                    defaultValue:
                      "Solo líneas con fase CP en alerta: naranja (≤10 días) o rojo (≤5 días) hasta la fecha planificada.",
                  })}
                >
                  <Chip
                    size="small"
                    label={t("NotasResumenPage.onlyAlert", {
                      defaultValue: "Alertas",
                    })}
                    clickable
                    onClick={() => setOnlyAlert((v) => !v)}
                    sx={toggleChipSx(onlyAlert, "warning")}
                  />
                </Tooltip>
                <Tooltip
                  title={t("NotasResumenPage.showClosedHint", {
                    defaultValue:
                      "Ver solo líneas cerradas manualmente desde el CP (al desactivar se muestran las abiertas).",
                  })}
                >
                  <Chip
                    size="small"
                    label={t("NotasResumenPage.showClosed", {
                      defaultValue: "Cerrados",
                    })}
                    clickable
                    onClick={handleToggleShowClosed}
                    sx={toggleChipSx(showClosedEntities, "secondary")}
                  />
                </Tooltip>

                <Tooltip title={t("NotasResumenPage.legendTitle", { defaultValue: "Leyenda" })}>
                  <IconButton
                    size="small"
                    onClick={(e) => setLegendAnchorEl(e.currentTarget)}
                    sx={{
                      width: 36,
                      height: 36,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.85),
                      bgcolor: alpha(theme.palette.background.paper, 0.92),
                    }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Popover
          open={Boolean(filtersAnchorEl)}
          anchorEl={filtersAnchorEl}
          onClose={() => setFiltersAnchorEl(null)}
          disableScrollLock
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                width: { xs: "min(100vw - 24px, 420px)", sm: 420 },
                overflow: "hidden",
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.16)}`,
              },
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                theme.palette.mode === "dark" ? 0.14 : 0.08,
              )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                    color: theme.palette.primary.dark,
                  }}
                >
                  <FilterListOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {t("NotasResumenPage.filtersLabel", {
                      defaultValue: "Filtros",
                    })}
                  </Typography>
                  {activeFilterCount > 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 600 }}
                    >
                      {t("NotasResumenPage.filtersActiveCount", {
                        count: activeFilterCount,
                        defaultValue: "{{count}} activos",
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t("NotasResumenPage.filtersHint", {
                        defaultValue: "Refina el listado del hub",
                      })}
                    </Typography>
                  )}
                </Box>
              </Stack>
              {activeFilterCount > 0 ? (
                <Button
                  size="small"
                  color="primary"
                  onClick={clearAdvancedFilters}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    minWidth: 0,
                    px: 1,
                  }}
                >
                  {t("NotasResumenPage.clearFilters", {
                    defaultValue: "Limpiar",
                  })}
                </Button>
              ) : null}
            </Stack>
          </Box>

          <Box sx={{ px: 1.25, py: 1.25, maxHeight: "min(70vh, 520px)", overflowY: "auto" }}>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                px: 1,
                pt: 0.25,
                pb: 0.5,
                fontSize: "0.62rem",
                fontWeight: 800,
                letterSpacing: 0.8,
                color: "text.secondary",
              }}
            >
              {t("NotasResumenPage.filtersSectionLine", {
                defaultValue: "Línea CP",
              })}
            </Typography>
            <Stack spacing={0.25} sx={{ mb: 1 }}>
              <NotasResumenFilterRow
                label={t("NotasResumenPage.proveedor", {
                  defaultValue: "Proveedor",
                })}
                value={proveedorFilter}
                onChange={applyFilterValue(setProveedorFilter)}
                active={isFilterActive(proveedorFilter)}
                theme={theme}
                selectMenuProps={selectMenuProps}
                allLabel={allFiltersLabel}
              >
                <MenuItem value="ALL">{allFiltersLabel}</MenuItem>
                {proveedorOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </NotasResumenFilterRow>
              <NotasResumenFilterRow
                label={t("NotasResumenPage.presupuestoEstado", {
                  defaultValue: "Estado presupuesto",
                })}
                value={presupuestoEstadoFilter}
                onChange={applyFilterValue(setPresupuestoEstadoFilter)}
                active={isFilterActive(presupuestoEstadoFilter)}
                theme={theme}
                selectMenuProps={selectMenuProps}
                allLabel={allFiltersLabel}
                formatValue={(v) =>
                  v === "ALL"
                    ? allFiltersLabel
                    : t(`NotasResumenPage.presupuestoEstado_${v}`, {
                        defaultValue: v,
                      })
                }
              >
                {PRESUPUESTO_ESTADO_FILTER_VALUES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value === "ALL"
                      ? allFiltersLabel
                      : t(`NotasResumenPage.presupuestoEstado_${value}`, {
                          defaultValue: value,
                        })}
                  </MenuItem>
                ))}
              </NotasResumenFilterRow>
            </Stack>

            <Divider sx={{ my: 0.75, opacity: 0.7 }} />

            <Typography
              variant="overline"
              sx={{
                display: "block",
                px: 1,
                pt: 0.25,
                pb: 0.5,
                fontSize: "0.62rem",
                fontWeight: 800,
                letterSpacing: 0.8,
                color: "text.secondary",
              }}
            >
              {t("NotasResumenPage.filtersSectionNotes", {
                defaultValue: "Notas",
              })}
            </Typography>
            <Stack spacing={0.25}>
              <NotasResumenFilterRow
                label={t("Notes.Tipo", { defaultValue: "Tipo" })}
                value={tipoNota}
                onChange={applyFilterValue(setTipoNota)}
                active={isFilterActive(tipoNota)}
                theme={theme}
                selectMenuProps={selectMenuProps}
                allLabel={allFiltersLabel}
                formatValue={(v) =>
                  v === "ALL"
                    ? allFiltersLabel
                    : v === "Remark"
                      ? t("Notes.remark")
                      : t("Notes.Open issue")
                }
              >
                <MenuItem value="ALL">{allFiltersLabel}</MenuItem>
                <MenuItem value="Remark">{t("Notes.remark")}</MenuItem>
                <MenuItem value="Open Issue">{t("Notes.Open issue")}</MenuItem>
              </NotasResumenFilterRow>
              <NotasResumenFilterRow
                label={t("Notes.Status", { defaultValue: "Estado" })}
                value={status}
                onChange={applyFilterValue(setStatus)}
                active={isFilterActive(status)}
                theme={theme}
                selectMenuProps={selectMenuProps}
                allLabel={allFiltersLabel}
                formatValue={(v) => {
                  if (v === "ALL") return allFiltersLabel;
                  if (v === "Open") return t("Notes.Open");
                  if (v === "In Progress") return t("Notes.In Progress");
                  return t("Notes.Closed");
                }}
              >
                <MenuItem value="ALL">{allFiltersLabel}</MenuItem>
                <MenuItem value="Open">{t("Notes.Open")}</MenuItem>
                <MenuItem value="In Progress">{t("Notes.In Progress")}</MenuItem>
                <MenuItem value="Closed">{t("Notes.Closed")}</MenuItem>
              </NotasResumenFilterRow>
              <NotasResumenFilterRow
                label={t("Notes.Responsible", { defaultValue: "Responsable" })}
                value={responsible}
                onChange={applyFilterValue(setResponsible)}
                active={isFilterActive(responsible)}
                theme={theme}
                selectMenuProps={selectMenuProps}
                allLabel={allFiltersLabel}
              >
                <MenuItem value="ALL">{allFiltersLabel}</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.username}>
                    {u.username}
                  </MenuItem>
                ))}
              </NotasResumenFilterRow>
            </Stack>
          </Box>
        </Popover>

        <Popover
          open={Boolean(legendAnchorEl)}
          anchorEl={legendAnchorEl}
          onClose={() => setLegendAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                p: 1.5,
                borderRadius: 2,
                minWidth: 220,
                border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              },
            },
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
            {t("NotasResumenPage.legendTitle", { defaultValue: "Leyenda" })}
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {legendItems.map((item) => (
              <Stack
                key={item.label}
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Popover>
      </Box>

      {hubLoading ? (
        <LinearProgress
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            height: 2,
            flexShrink: 0,
          }}
        />
      ) : null}

      {/* ============== LISTADO SCROLL ============== */}
      <Box sx={pageScrollSx}>
        {hubLoadError ? (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              borderColor: "error.main",
            }}
          >
            <Typography color="error" variant="body1" fontWeight={600}>
              {t("NotasResumenPage.loadError", {
                defaultValue: "No se pudo cargar el hub de notas.",
              })}
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              {hubLoadError}
            </Typography>
          </Paper>
        ) : hubLoading ? (
          [...Array(4)].map((_, i) => <NotasResumenSkeleton key={i} />)
        ) : filteredBlocks.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              borderStyle: "dashed",
            }}
          >
            <NotesIcon
              sx={{
                fontSize: 40,
                color: "action.disabled",
                opacity: 0.6,
                mb: 1,
              }}
            />
            <Typography color="text.secondary" variant="body1" fontWeight={600}>
              {hubCliente
                ? t("NotasResumenPage.noEntitiesForClient", {
                    defaultValue: "No hay entidades para este cliente",
                  })
                : t("NotasResumenPage.noEntities", {
                    defaultValue: "No hay entidades en el hub",
                  })}
            </Typography>
          </Paper>
        ) : (
        filteredBlocks.map(({ entityKey, displayNotas }) => {
          const block = grouped[entityKey];
          if (!block) return null;
          const notasForDisplay = displayNotas ?? block.notas ?? [];
          const isOpen = openCards[entityKey] ?? false;
          const unreadCount = unreadCountByEntity(entityKey);
          const badge = getNoteBadgeState(block, unreadCount);
          const { variant, noteCount, openIssueCount } = badge;
          const hasAttachments =
            block.hasAttachments ||
            productsWithAttachments.has(entityKey);
          const clientName = block.productInfo?.Cliente || "—";
          const supplierName = block.productInfo?.Proveedor || "—";
          const designLabel =
            block.productInfo?.Design ||
            block.productInfo?.NumeroPresupuesto ||
            (block.productId ? `Producto ${block.productId}` : block.entityKey);
          const chatMeta = getBlockChatMeta(block);
          const clientColor = getClientColor(clientName);
          const supplierColor = getSupplierColor(supplierName);
          const fechasEntry = fechasByKey[entityKey];
          const fechasDisplay =
            fechasEntry?.loaded && Object.keys(fechasEntry.fechas || {}).length
              ? fechasEntry.fechas
              : EMPTY_FECHAS_PLACEHOLDER;
          const fechasPending = !fechasEntry?.loaded;
          const showLegacyLogistics =
            Boolean(block.hasLegacyLogistics) || Boolean(block.legacyPoDate);

          return (
            <Paper
              key={entityKey}
              variant="outlined"
              sx={{
                position: "relative",
                mb: 1.75,
                display: "flex",
                flexDirection: "column",
                borderRadius: 2.5,
                overflow: "hidden",
                contentVisibility: "auto",
                containIntrinsicSize: "0 140px",
                borderColor: isOpen
                  ? alpha(theme.palette.primary.main, 0.5)
                  : "divider",
                backgroundColor: theme.palette.background.paper,
                transition:
                  "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 2px 10px rgba(0,0,0,0.35)"
                    : "0 1px 4px rgba(0,0,0,0.05)",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                  boxShadow: `0 6px 18px ${alpha(
                    theme.palette.common.black,
                    theme.palette.mode === "dark" ? 0.4 : 0.08,
                  )}`,
                },
              }}
            >
              <VisibleFechasTrigger
                entityKey={entityKey}
                enabled={fechasPending}
                onVisible={requestFechasLoad}
              />
              {/* ================= CABECERA ================= */}
              <Box
                onClick={() => toggleCard(entityKey)}
                sx={{
                  px: { xs: 1.5, md: 2 },
                  py: 1.5,
                  cursor: "pointer",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "180px 1fr",
                    md: "180px minmax(160px, 1.1fr) 3fr auto",
                  },
                  alignItems: "center",
                  gap: { xs: 1.5, md: 2 },
                  background: isOpen
                    ? `linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === "dark" ? 0.12 : 0.05,
                      )} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`
                    : "transparent",
                }}
              >
                {/* === Indicadores + foto === */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <CpChatDot
                    size={22}
                    noLeidos={chatMeta.noLeidos}
                    hasMessages={chatMeta.hasMessages}
                    hasMention={chatMeta.hasMention}
                    onClick={() => openCpChatFromBlock(block, "mensajes")}
                  />

                  {/* Indicador notas: rojo nuevas · naranja incidencias · verde normales */}
                  <Tooltip
                    title={
                      variant === "empty"
                        ? t("NotasResumenPage.noNotesInLine", {
                            defaultValue: "Sin notas en esta línea",
                          })
                        : variant === "unread"
                          ? t("NotasResumenPage.notesUnreadTooltip", {
                              total: noteCount,
                              unread: unreadCount,
                              defaultValue:
                                "{{total}} notas · {{unread}} sin leer",
                            })
                          : variant === "issue"
                            ? t("NotasResumenPage.notesIssueTooltip", {
                                total: noteCount,
                                issues: openIssueCount,
                                defaultValue:
                                  "{{total}} notas · {{issues}} incidencias abiertas",
                              })
                            : t("NotasResumenPage.notesReadTooltip", {
                                count: noteCount,
                                defaultValue: "{{count}} notas · todo leído",
                              })
                    }
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        openCpChatFromBlock(block, "notas");
                      }}
                      sx={{
                        minWidth: 22,
                        height: 22,
                        px: noteCount > 9 ? 0.5 : 0,
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        flexShrink: 0,
                        cursor: "pointer",
                        color:
                          variant === "unread"
                            ? theme.palette.common.white
                            : variant === "issue"
                              ? theme.palette.warning.dark
                              : variant === "normal"
                                ? theme.palette.success.dark
                                : theme.palette.text.disabled,
                        bgcolor:
                          variant === "unread"
                            ? theme.palette.error.main
                            : variant === "issue"
                              ? alpha(
                                  theme.palette.warning.main,
                                  theme.palette.mode === "dark" ? 0.42 : 0.28,
                                )
                              : variant === "normal"
                                ? alpha(
                                    theme.palette.success.main,
                                    theme.palette.mode === "dark" ? 0.38 : 0.22,
                                  )
                                : alpha(theme.palette.text.disabled, 0.25),
                        boxShadow:
                          variant === "unread"
                            ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.2)}`
                            : variant === "issue"
                              ? `0 0 0 2px ${alpha(theme.palette.warning.main, 0.16)}`
                              : variant === "normal"
                                ? `0 0 0 2px ${alpha(theme.palette.success.main, 0.1)}`
                                : "none",
                      }}
                    >
                      {noteCount}
                    </Box>
                  </Tooltip>

                  {/* Indicador adjuntos */}
                  <Tooltip
                    title={
                      hasAttachments
                        ? t("Notes.Attachments")
                        : t("Notes.NoAttachments")
                    }
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasAttachments) {
                          openAttachmentsDialog(block.entityKey);
                        }
                      }}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: hasAttachments
                          ? alpha(theme.palette.info.main, 0.16)
                          : alpha(theme.palette.text.disabled, 0.12),
                        color: hasAttachments
                          ? theme.palette.info.main
                          : theme.palette.text.disabled,
                        border: "1px solid",
                        borderColor: hasAttachments
                          ? alpha(theme.palette.info.main, 0.4)
                          : "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: hasAttachments ? "pointer" : "default",
                        flexShrink: 0,
                        transition: "background-color 0.15s ease",
                        "&:hover": hasAttachments
                          ? {
                              backgroundColor: alpha(
                                theme.palette.info.main,
                                0.25,
                              ),
                            }
                          : null,
                      }}
                    >
                      <AttachFileOutlinedIcon sx={{ fontSize: 13 }} />
                    </Box>
                  </Tooltip>

                  {showLegacyLogistics ? (
                    <Tooltip
                      title={t(
                        "CriticalPathHorizontal.tooltipLegacyLogistics",
                        { defaultValue: "Logística legacy" },
                      )}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          bgcolor: alpha(theme.palette.warning.main, 0.16),
                          color: "warning.main",
                          border: "1px solid",
                          borderColor: alpha(theme.palette.warning.main, 0.4),
                        }}
                      >
                        <LocalShippingOutlinedIcon sx={{ fontSize: 13 }} />
                      </Box>
                    </Tooltip>
                  ) : null}

                  {/* Foto producto (espacio reservado para hasta 2 imágenes) */}
                  <Box
                    sx={{
                      width: 108,
                      minWidth: 108,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      overflow: "hidden",
                    }}
                  >
                    <ProductPhotosCell
                      deferUntilVisible
                      numPresupuesto={block.productInfo.NumeroPresupuesto}
                      idMuestra={block.idMuestra}
                      temporada={block.productInfo.Temporada}
                      marca={block.productInfo.Cliente}
                      designer={block.productInfo.Design}
                      proveedor={block.productInfo.Proveedor}
                      onImageClick={(anchor, url) => {
                        setSelectedImage(url);
                        setImageDialogOpen(true);
                      }}
                    />
                  </Box>
                </Stack>

                {/* === Identidad: avatares + diseño + estado presupuesto === */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0, overflow: "hidden" }}
                >
                  <Tooltip title={clientName}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        color: "#fff",
                        backgroundColor: clientColor,
                        boxShadow: `0 2px 6px ${alpha(clientColor, 0.35)}`,
                        border: `1px solid ${alpha("#000", 0.08)}`,
                        flexShrink: 0,
                        userSelect: "none",
                      }}
                    >
                      {getEntityInitial(clientName)}
                    </Box>
                  </Tooltip>

                  <Tooltip title={`${t("Notes.Supplier", { defaultValue: "Proveedor" })} · ${supplierName}`}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        color: "#fff",
                        backgroundColor: supplierColor,
                        flexShrink: 0,
                        boxShadow: `0 1px 3px ${alpha(supplierColor, 0.45)}`,
                      }}
                    >
                      {getEntityInitial(supplierName)}
                    </Box>
                  </Tooltip>

                  <Box sx={{ overflow: "hidden", minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.88rem",
                        lineHeight: 1.25,
                        color: theme.palette.text.primary,
                      }}
                      noWrap
                    >
                      {designLabel}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ mt: 0.35, minWidth: 0, flexWrap: "wrap" }}
                    >
                      {block.productInfo.Temporada ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.68rem", fontWeight: 600 }}
                        >
                          {block.productInfo.Temporada}
                        </Typography>
                      ) : null}
                      <PresupuestoSemaforo estado={block.presupuestoEstado} />
                    </Stack>
                  </Box>
                </Stack>

                {/* === Fechas === */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(4, 1fr)",
                      md: "repeat(5, 1fr)",
                      lg: "repeat(11, 1fr)",
                    },
                    gap: 0.75,
                    p: 0.25,
                  }}
                >
                  {Object.entries(fechasDisplay).map(([key, value]) => {
                    const dateColor = phaseSemaforoColor(value);
                    const hasDate = !!value?.date;
                    const chipColor = hasDate
                      ? dateColor
                      : theme.palette.text.disabled;
                    return (
                      <Box
                        key={key}
                        sx={{
                          textAlign: "center",
                          border: "1px solid",
                          borderColor: hasDate
                            ? alpha(chipColor, 0.35)
                            : "divider",
                          borderRadius: 1.5,
                          backgroundColor: hasDate
                            ? alpha(
                                chipColor,
                                theme.palette.mode === "dark" ? 0.12 : 0.08,
                              )
                            : alpha(
                                theme.palette.text.primary,
                                theme.palette.mode === "dark" ? 0.04 : 0.02,
                              ),
                          py: 0.25,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.62rem",
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                            color: "text.secondary",
                            lineHeight: 1.2,
                            pt: 0.25,
                          }}
                        >
                          {key}
                        </Typography>
                        {fechasPending ? (
                          <Skeleton
                            variant="text"
                            width="72%"
                            height={16}
                            sx={{ mx: "auto", mt: 0.25 }}
                          />
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              color: hasDate ? dateColor : "text.disabled",
                              fontWeight: 700,
                              lineHeight: 1.4,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {hasDate
                              ? formatHubFechaCell(value.date)
                              : "—"}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* === Acciones === */}
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{ justifySelf: "end", flexShrink: 0 }}
                >
                  <Tooltip
                    title={
                      block.isClosed
                        ? t("CriticalPathHorizontal.tooltipReopenCp", {
                            defaultValue: "Reabrir en hub",
                          })
                        : t("CriticalPathHorizontal.tooltipCloseCp", {
                            defaultValue: "Cerrar en hub",
                          })
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestToggleEntityClosed(block);
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        color: block.isClosed ? "error.main" : "success.main",
                        border: "1px solid",
                        borderColor: alpha(
                          block.isClosed
                            ? theme.palette.error.main
                            : theme.palette.success.main,
                          0.35,
                        ),
                      }}
                    >
                      {block.isClosed ? (
                        <VisibilityOutlined fontSize="small" />
                      ) : (
                        <VisibilityOffOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("Notes.Notes", { defaultValue: "Notas" })}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCpChatFromBlock(block, "notas");
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        backgroundColor: alpha(theme.palette.info.main, 0.12),
                        color: theme.palette.info.main,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.info.main, 0.22),
                        },
                      }}
                    >
                      <NotesIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("NotasResumenPage.openCriticalPath")}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        void openCriticalPathFromBlock(block);
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        backgroundColor: alpha(
                          theme.palette.warning.main,
                          0.12,
                        ),
                        color: theme.palette.warning.main,
                        border: `1px solid ${alpha(
                          theme.palette.warning.main,
                          0.3,
                        )}`,
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.warning.main,
                            0.22,
                          ),
                        },
                      }}
                    >
                      <TimelineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Indicador colapsado/expandido */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                      transition: "transform 0.2s ease",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    <KeyboardArrowDownOutlinedIcon fontSize="small" />
                  </Box>
                </Stack>
              </Box>

              {/* ================= NOTAS DENTRO ================= */}
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Divider />
                <Box sx={{ px: { xs: 1.5, md: 2 }, py: 2 }}>
                  {block.notasLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton variant="rounded" width="100%" height={56} />
                    </Stack>
                  ) : notasForDisplay.length ? (
                    notasForDisplay.map((n) => {
                    const isOpenIssue = n.TipoNota === "Open Issue";
                    const accent = isOpenIssue
                      ? theme.palette.error.main
                      : theme.palette.info.main;
                    return (
                      <Box
                        key={n.Id}
                        sx={{
                          mb: 1,
                          p: 1.25,
                          borderRadius: 1.5,
                          backgroundColor: alpha(
                            accent,
                            theme.palette.mode === "dark" ? 0.08 : 0.04,
                          ),
                          border: `1px solid ${alpha(accent, 0.2)}`,
                          borderLeft: `3px solid ${accent}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
                        >
                          <Chip
                            label={n.TipoNota}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              backgroundColor: alpha(accent, 0.16),
                              color: accent,
                              border: `1px solid ${alpha(accent, 0.35)}`,
                            }}
                          />
                          <Chip
                            label={n.Status}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              backgroundColor: alpha(
                                theme.palette.text.primary,
                                0.06,
                              ),
                              color: theme.palette.text.secondary,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {n.Responsible}
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {dayjs(n.FechaNota).format("DD/MM/YYYY")}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8rem",
                            color: theme.palette.text.primary,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {n.Nota}
                        </Typography>
                      </Box>
                    );
                  })
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      {t("NotasResumenPage.noNotesInLine", {
                        defaultValue: "Sin notas en esta línea",
                      })}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })
        )}
      </Box>

      {/* Chat + Notas unificado */}
      <CpEntityChat
        open={cpChatState.open}
        productId={cpChatState.productId}
        idPresupuestoA3Pedido={cpChatState.idPresupuestoA3Pedido}
        title={cpChatState.title}
        subtitle={cpChatState.subtitle}
        initialTab={cpChatState.initialTab}
        onNotasChanged={({
          changed,
          productId,
          idMuestra,
          idPresupuestoA3Pedido,
        }) => {
          if (!changed) return;
          const key = idPresupuestoA3Pedido
            ? `D_${idPresupuestoA3Pedido}`
            : productId
              ? `P_${productId}`
              : idMuestra
                ? `M_${idMuestra}`
                : null;
          if (key) refreshEntityNotas(key);
        }}
        onClose={() => {
          const { productId, idPresupuestoA3Pedido } = cpChatState;
          setCpChatState({
            open: false,
            productId: null,
            idPresupuestoA3Pedido: null,
            title: "",
            subtitle: "",
            initialTab: "notas",
          });
          const key = idPresupuestoA3Pedido
            ? `D_${idPresupuestoA3Pedido}`
            : productId
              ? `P_${productId}`
              : null;
          if (key) refreshEntityNotas(key);
          loadChatSummary();
        }}
      />

      {/* DIALOG IMAGEN */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => {
          setImageDialogOpen(false);
          setSelectedImage(null);
        }}
        maxWidth="md" // 👈 TAMAÑO REAL DEL DIALOG
        fullWidth // 👈 ocupa todo ese maxWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#000", // opcional, queda 🔥
          }}
        >
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Product"
              style={{
                maxWidth: "100%",
                height: "40em", // 👈 AQUÍ está el tamaño real
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG ADJUNTOS ===== */}
      <Dialog
        open={attachmentsDialogOpen}
        onClose={() => setAttachmentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("Notes.Attachments")}</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: 2,
            }}
          >
            {attachments.map((f) => (
              <Box key={f.Id}>
                {f.MimeType?.startsWith("image/") ? (
                  <img
                    src={`${process.env.REACT_APP_API_URL}/uploads/${f.FilePath}`}
                    alt={f.FileName}
                    style={{
                      width: "100%",
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <a
                    href={`${process.env.REACT_APP_API_URL}/uploads/${f.FilePath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📎 {f.FileName}
                  </a>
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={closeConfirmState.open}
        onClose={() => setCloseConfirmState({ open: false, block: null })}
      >
        <DialogTitle>
          {t("CriticalPathHorizontal.closeDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("CriticalPathHorizontal.closeDialogBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCloseConfirmState({ open: false, block: null })}
          >
            {t("CriticalPathHorizontal.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmCloseEntity}
          >
            {t("CriticalPathHorizontal.closeLine")}
          </Button>
        </DialogActions>
      </Dialog>

      {drawerOpen && (drawerProductId || drawerIdMuestra) && (
        <CriticalPathDrawer
          key={`${drawerMode}_${drawerProductId ?? ""}_${drawerIdMuestra ?? ""}_${drawerIdPresupuesto ?? ""}_${drawerIdPresupuestoA3Pedido ?? ""}`}
          open={drawerOpen}
          mode={drawerMode}
          productId={drawerProductId}
          idMuestra={drawerIdMuestra}
          idPresupuesto={drawerIdPresupuesto}
          idPresupuestoA3Pedido={drawerIdPresupuestoA3Pedido}
          legacyLogistics={drawerLegacyLogistics}
          hasLegacyLogisticsRow={drawerHasLegacyLogistics}
          gridRowSnapshot={drawerGridRowSnapshot}
          muestraHeaderInfo={drawerMuestraHeader}
          onDirty={() => {}}
          onClose={async () => {
            setDrawerOpen(false);

            const entityKey = drawerEntityKeyRef.current;
            invalidateCpProdRowsCache();

            if (entityKey) {
              const block = groupedRef.current[entityKey];
              if (block?.idMuestra) {
                await refreshSingleMuestra(block.idMuestra);
              } else if (block) {
                const cpRow = await resolveCpRowForBlockSafe(block);
                if (cpRow) patchBlockCpMeta(entityKey, cpRow);
                const refreshedBlock = groupedRef.current[entityKey] || block;
                const row = await buildFechasForBlock(refreshedBlock);
                if (row) applyFechasRow(row);
              }
            }

            drawerEntityKeyRef.current = null;
            setDrawerProductId(null);
            setDrawerIdMuestra(null);
            setDrawerIdPresupuesto(null);
            setDrawerIdPresupuestoA3Pedido(null);
            setDrawerLegacyLogistics([]);
            setDrawerHasLegacyLogistics(false);
            setDrawerGridRowSnapshot(null);
            setDrawerMuestraHeader(null);
          }}
        />
      )}
    </Box>
  );
};

export default NotasResumen;
